import { ChangeDetectorRef, Injectable } from '@angular/core';
import {
  Resolve,
  RouterStateSnapshot,
  ActivatedRouteSnapshot,
  ActivatedRoute,
} from '@angular/router';
import { ApiService } from '@app/shared/services/api.service';
import { APIRequest } from '@app/shared/services/req-res.types';
import { UtilService } from '@app/shared/services/util.service';
import { Observable, of } from 'rxjs';
import { forkJoin } from 'rxjs';
import { URL } from '@app-shared/constants/service-urls';
import { catchError, map, switchMap } from 'rxjs/operators';
import { get, groupBy } from 'lodash';
import moment from 'moment';
import { UNLISTED_KEYS } from '../constants/application-constants';
import { content } from 'html2canvas/dist/types/css/property-descriptors/content';

@Injectable({
  providedIn: 'root'
})
export class RevisionHistoryResolver implements Resolve<[]> {

  events: any;
  parentUrl: string;
  adminUrl: boolean = false;
  unlistedKeys: any = UNLISTED_KEYS;
  versionMap: Map<number, string> = new Map(); // Map to store version_id -> version_name
  tagMap: Map<number, string> = new Map(); // Map to store tag id -> display name
  constructor(
    private api: ApiService,
    private util: UtilService,
    private route: ActivatedRoute,

  ) { }

  resolve(route: ActivatedRouteSnapshot): Observable<any> {
    /* Getting URL Params*/
    this.parentUrl = get(route.parent, 'url[0].path');
    if (this.parentUrl === 'Courses') {
      this.parentUrl = get(route.url, '[0].path');
    }
    if (!this.parentUrl) {
      this.findPath(route);
    } else {
      // Check if we need to map the parentUrl for specific routes
      this.findPath(route);
    }

    // Get orgId from query params for MOU history
    if (this.parentUrl === 'manageMOUHistory') {
      const orgId = route.queryParamMap.get('orgId');
      if (orgId) {
        localStorage.setItem('SelectedOrgId', orgId);
      }
    }

    // Get orgId from query params for admin revision history (system super admins)
    if (this.parentUrl === 'manageAdminHistory') {
      const orgId = route.queryParamMap.get('orgId');
      if (orgId) {
        localStorage.setItem('SelectedOrgId', orgId);
      }
    }

    // Get orgId from query params for system admin revision history
    if (this.parentUrl === 'manageSystemAdminHistory') {
      const orgId = route.queryParamMap.get('orgId');
      if (orgId) {
        localStorage.setItem('SelectedOrgId', orgId);
      }
    }

    return this.getRevisionHistory(route.paramMap.get('id'));
  }

  findPath(route: ActivatedRouteSnapshot) {
    switch (route.routeConfig.path) {
      case 'org-admin/RevisionHistory/:id/:name':
        this.parentUrl = 'manageAdminHistory';
        this.adminUrl = true;
        break;
      case 'system-admin/RevisionHistory/:id/:name':
        this.parentUrl = 'manageSystemAdminHistory';
        this.adminUrl = true;
        break;
      case 'QuestionRevisionHistory/:id/:name':
        this.parentUrl = 'questions'; // This should match backend
        break;
      case 'labelRevisionHistory/:id/:name':
        this.parentUrl = 'label'; // This should match backend
        break;
      case 'tagRevisionHistory/:id/:name':
        this.parentUrl = 'tags';
        break;
      case 'org-mou/RevisionHistory/:id/:name':
        this.parentUrl = 'manageMOUHistory';
        this.adminUrl = true;
        break;

      case 'AssessmentPlanRevisionHistory/:id/:name':
        this.parentUrl = 'assessmentPlan';
        break;
      case 'ProjectsRevisionHistory/:id/:name':
        this.parentUrl = 'Project';
        break;
      case 'EvaluationCriteriaRevisionHistory/:id/:name':
        this.parentUrl = 'EvaluationCriteria';
        break;
      case 'api-integration/revision-history/:id/:name':
        this.parentUrl = 'apiIntegration';
        break;
      case 'BillsPaymentRevisionHistory/:id/:name':
        this.parentUrl = 'Payments';
        break;
      case 'InvoicesRevisionHistory/:id/:name':
        this.parentUrl = 'Invoices';
        break;
      case 'ReceiptsRevisionHistory/:id/:name':
        this.parentUrl = 'Receipts';
        break;
      case 'subscriptions/RevisionHistory/:id/:name':
        this.parentUrl = 'subscriptionPlans';
        console.log('Subscription Plans route detected, set parentUrl to subscriptionPlans');
        break;
      case 'QuestionPaperVersionRevisionHistory/:id/:name':
        this.parentUrl = 'questionPaperVersions';
        break;
      case 'CourseRevisionHistory/:id/:name':
        this.parentUrl = 'CourseRevisionHistory';
        break;
      case 'CourseVersionRevisionHistory/:id/:name':
        this.parentUrl = 'CourseVersionRevisionHistory';
        break;
      case 'CourseModuleRevisionHistory/:id/:name':
        this.parentUrl = 'CourseModuleRevisionHistory';
        break;
      case 'CourseTopicRevisionHistory/:id/:name':
        this.parentUrl = 'CourseTopicRevisionHistory';
        break;

      default:
        // If parentUrl is 'AssessmentPlan' and we're in a revision history route, map it
        if (this.parentUrl === 'AssessmentPlan' && route.routeConfig.path?.includes('RevisionHistory')) {
          this.parentUrl = 'assessmentPlan';
        }
        break;
    }

    // Additional check for AssessmentPlan revision history
    if (route.routeConfig.path === 'AssessmentPlanRevisionHistory/:id/:name') {
      this.parentUrl = 'assessmentPlan';
    }
  }
  removeNullEntries(entries: any):any  {
    const result = {};
    for (let key in entries) {
      if (entries[key]) {
        result[key] = entries[key];
      }
    }
    return result;
  }

  findChangedKeys(oldObj, newObj) {
    const changedKeys = [];

    for (const key in newObj) {
      if (newObj.hasOwnProperty(key)) {
        if (!oldObj.hasOwnProperty(key) || !this.areValuesEffectivelySame(oldObj[key], newObj[key])) {
          changedKeys.push(key);
        }
      }
    }
    return changedKeys;
  }

  getLabelName(key: any, element: any) {
    let keyName = key;
    if (
      this.parentUrl === 'CourseRevisionHistory' ||
      this.parentUrl === 'CourseVersionRevisionHistory' ||
      this.parentUrl === 'CourseModuleRevisionHistory' ||
      this.parentUrl === 'CourseTopicRevisionHistory'
    ) {
      if (keyName === 'name') return 'Title';
      if (keyName === 'is_active') return 'Status';
    }

    // QuestionPaperVersions-specific field mappings
    if (this.parentUrl === 'questionPaperVersions') {
      switch (keyName) {
        case 'name': return 'Name';
        case 'is_active': return 'Is Active';
        case 'is_deleted': return 'Is Deleted';
        case 'is_archived': return 'Is Archived';
        case 'is_published': return 'Is Published';
        case 'has_evaluation': return 'Has Evaluation';
        case 'time_limit': return 'Time Limit';
        case 'total_marks': return 'Total Marks';
        case 'no_of_versions': return 'No. of Versions';
        case 'count_of_versions': return 'Count Of Versions';
        case 'count_of_questions': return 'Count Of Questions';
        case 'count_of_question_overlap': return 'Count Of Question Overlap';
        case 'category': return 'Assign Marks';
        case 'grouping_criteria': return 'Assign Grouping Criteria';
        case 'question_grouping_logic': return 'Question Grouping Logic';
        case 'instructions': return 'Instructions';
        case 'question_paper_instruction': return 'Instructions';
        case 'short_name': return 'Short Name';
        case 'course_name': return 'Course Name';
        case 'course_version_name': return 'Course Version Name';
        case 'organization_name': return 'Organization Name';
        case 'created_user_name': return 'Created User Name';
        case 'created_user_username': return 'Created User Email';
        case 'created_by_name': return 'Created By Name';
        case 'created_by_username': return 'Created By Email';
        case 'modified_user_name': return 'Modified User Name';
        case 'modified_user_username': return 'Modified User Email';
        case 'modified_by_name': return 'Modified By Name';
        case 'modified_by_username': return 'Modified By Email';
        case 'created_datetime': return 'Created Datetime';
        case 'label_name': return 'LabelName';
      }
    }

    if (this.parentUrl === 'subscriptionPlans') {
      switch (keyName) {
        case 'created_user_name': return 'Created User Name';
        case 'created_user_username': return 'Created User Email';
        case 'created_by_name': return 'Created By Name';
        case 'created_by_username': return 'Created By Email';
        case 'modified_user_name': return 'Modified User Name';
        case 'modified_user_username': return 'Modified User Email';
        case 'modified_by_name': return 'Modified By Name';
        case 'modified_by_username': return 'Modified By Email';
        case 'created_datetime': return 'Created Datetime';
        case 'modified_datetime': return 'Modified Datetime';
      }
    }

    // EvaluationCriteria-specific field mappings
    if (this.parentUrl === 'EvaluationCriteria' || element?.audit_table_name === 'question_paper_evaluation_criteria') {
      switch (keyName) {
        case 'name': return 'Alias';
        case 'description': return 'Description';
        case 'is_active': return 'Status';
        case 'evaluation_criteria': return 'Evaluation Criteria';
        case 'grading_criteria': return 'Grading Criteria';
        case 'tags': return 'Tags';
      }
    }

    // Label-specific field mappings
    if (this.parentUrl === 'label') {
      switch (keyName) {
        case 'label_name':
          return 'Label Name';
        case 'label_id':
          return 'Label ID';
        case 'parent_label_id':
          return 'Parent Label ID';
        case 'label_index':
          return 'Label Index';
        case 'course_id':
          return 'Course ID';
        case 'version_id':
          return 'Version ID';
        case 'is_active':
          return 'Status';
        case 'modified_datetime':
          keyName = 'Modified Date&Time';
          element.newv['modified_datetime'] = element?.newv['modified_datetime'] ? moment(element?.newv['modified_datetime'])?.format("M/D/YYYY, h:mm:ss A") : null;
          element.oldv['modified_datetime'] = element?.oldv['modified_datetime'] ? moment(element?.oldv['modified_datetime'])?.format("M/D/YYYY, h:mm:ss A") : null;
          return keyName;
      }
    }

    // Invoices-specific field mappings
    if (this.parentUrl === 'Invoices') {
      switch (keyName) {
        case 'reference_no':
          return 'Invoice Number';
        case 'party_name':
          return 'Party Name';
        case 'customer_id':
          return 'Customer ID';
        case 'customer_name':
          return 'Customer Name';
        case 'product_id':
          return 'Product ID';
        case 'product_name':
          return 'Product Name';
        case 'rate_id':
          return 'Rate ID';
        case 'billing_address_id':
          return 'Billing Address ID';
        case 'invoice_date':
          return 'Invoice Date';
        case 'invoice_value':
          return 'Invoice Value';
        case 'total_amount':
          return 'Total Amount';
        case 'tds_deducted':
          return 'TDS Deducted';
        case 'units':
          return 'Units';
        case 'status':
          return 'Status';
        case 'service':
          return 'Service';
        case 'type':
          return 'Type';
        case 'gstin':
          return 'Customer GSTIN';
        case 'igst_percent':
          return 'IGST %';
        case 'cgst_percent':
          return 'CGST %';
        case 'sgst_percent':
          return 'SGST %';
        case 'remarks':
          return 'Remarks';
        case 'po_number':
          return 'PO Number';
        case 'item_ids':
          return 'Item IDs';
        case 'item':
          return 'Items / Batches';
        case 'created_datetime':
          return 'Created Datetime';
        case 'modified_datetime':
          return 'Modified Datetime';
        case 'is_deleted':
          return 'Is Deleted';
      }
    }
    
    switch (keyName) {
      case 'is_active':
        keyName = 'Status';
        break;

      case 'is_archived':
        keyName = 'Archived';
        break;

      case 'is_deleted':
        keyName = 'Deleted';
        break;

      case 'parent_question_id':
        keyName = 'Parent Question';
        break;

      case 'modified_datetime':
        keyName = 'Modified Date&Time'
        element.newv['modified_datetime'] = element?.newv['modified_datetime'] ? moment(element?.newv['modified_datetime'])?.format("M/D/YYYY, h:mm:ss A") : null;
        element.oldv['modified_datetime'] = element?.oldv['modified_datetime'] ? moment(element?.oldv['modified_datetime'])?.format("M/D/YYYY, h:mm:ss A") : null;
        break;
        
      default:
        keyName = this.util.capitalizeFirstLetter(keyName.split('_').join(' '))
        break;
    }

    return keyName;
  }

  private shouldHideDuplicateAuditField(key: string): boolean {
    const hiddenModules = new Set([
      'subscriptionPlans',
      'manageAdminHistory',
      'manageSystemAdminHistory',
      'orgAdmin',
    ]);
    const hiddenFields = new Set([
      'created_user_name',
      'created_user_username',
      'created_by_name',
      'created_by_username',
      'modified_user_name',
      'modified_user_username',
      'modified_by_name',
      'modified_by_username',
    ]);

    return hiddenModules.has(this.parentUrl) && hiddenFields.has(key);
  }

  // Build structured creation content for labels with hierarchy display
buildLabelCreationContent(element: any, allEntries: any[]): any {
  const labelData = element.newv;
  const creationContent: any[] = [];

  if (Array.isArray(labelData)) {
    const rootLabel = labelData.find(l => (l.parentLabelId === 0 || l.parent_label_id === 0 || l.isParent === true));
    const hierarchyLines: string[] = [];
    
    const buildTree = (parentId: number, level: number = 0): void => {
      const children = labelData
        .filter(l => {
          const parentLabelId = l.parentLabelId || l.parent_label_id;
          return parentLabelId === parentId && l.isParent !== true;
        })
        .sort((a, b) => (a.labelIndex || a.label_index || 0) - (b.labelIndex || b.label_index || 0));
      
      children.forEach((child, index) => {
        const id = child.labelId || child.id || child.label_id;
        const name = child.labelName || child.label_name;
        const indent = '&nbsp;&nbsp;'.repeat(level * 3);
        const branch = index === children.length - 1 ? '└─ ' : '├─ ';

        hierarchyLines.push(`<div>${indent}${branch}<strong>${name}</strong></div>`);
        buildTree(id, level + 1);
      });
    };

    // Handle case where rootLabel might be undefined (nested label creation)
    if (rootLabel) {
      const rootId = rootLabel?.labelId || rootLabel?.id || rootLabel?.label_id;
      hierarchyLines.push(`<div><strong>${rootLabel?.labelName || rootLabel?.label_name}</strong></div>`);
      buildTree(rootId, 1);
    } else {
      // If no root found, build tree from any label's parent information
      labelData.forEach(label => {
        if (!label.isParent) {
          const parentName = label.parentLabelName || 'Root';
          const name = label.labelName || label.label_name;
          hierarchyLines.push(`<div><strong>${parentName}</strong></div>`);
          hierarchyLines.push(`<div>&nbsp;&nbsp;└─ <strong>${name}</strong></div>`);
        }
      });
    }

    creationContent.push({
      label: 'Hierarchy Structure',
      newValue: `<div class="hierarchy-scroll-box">${hierarchyLines.join('')}</div>`
    });
  }

  const correctOrgName = this.getCorrectOrgNameForLabel(element);
  creationContent.push({ label: 'Organization', newValue: correctOrgName });

  const creatorName = element.created_user_name || element.created_by_name || element.created_user || element.modified_user || element.created_by || '';
  if (creatorName) {
    creationContent.push({ label: 'Created User', newValue: creatorName });
  }

  creationContent.push({ label: 'Created Date', newValue: this.formatDateTime(element.modified_date) });

  return creationContent;
}
  private getCorrectOrgNameForLabel(element: any): string {
    try {
      if (element.organization_name && element.organization_name.trim() !== '') {
        return element.organization_name;
      }
      const userOrgListStr = localStorage.getItem('userOrgList');
      if (!userOrgListStr) {
        return '@Tap Academy'; 
      }

      let userOrgList = [];
      try {
        userOrgList = JSON.parse(userOrgListStr);
      } catch (e) {
        return '@Tap Academy'; 
      }

      if (!Array.isArray(userOrgList) || userOrgList.length === 0) {
        return '@Tap Academy'; 
      }

      let currentUserId = localStorage.getItem('userId');
      if (!currentUserId) {
        currentUserId = element.modified_user_id || element.created_user_id;
      }

      if (currentUserId) {
        const matchingOrg = userOrgList.find((org: any) => {
          const orgUserId = String(org.user_id);
          const currentUserIdStr = String(currentUserId);
          return orgUserId === currentUserIdStr;
        });

        if (matchingOrg && matchingOrg.org_name) {
          return matchingOrg.org_name;
        }
      }

      if (userOrgList[0] && userOrgList[0].org_name) {
        return userOrgList[0].org_name;
      }

      return '@Tap Academy'; 
    } catch (error) {
      console.error('Error getting organization name:', error);
      return '@Tap Academy';
    }
  }
  // Helper to build multi-level hierarchy tree recursively
  private buildHierarchyTree(
    parentLabelId: number,
    courseId: number,
    versionId: number,
    allEntries: any[],
    level: number
  ): string {
    const children = this.findAllChildLabels(parentLabelId, courseId, versionId, allEntries);
    
    if (!children || children.length === 0) {
      return '';
    }

    const indent = '   '.repeat(level - 1);
    const lines: string[] = [];

    children.forEach((child, index) => {
      const isLast = index === children.length - 1;
      const connector = isLast ? '└─' : '├─';
      lines.push(`${indent}${connector} ${child.label_name} (Index: ${child.label_index})`);

      // Recursively add grandchildren
      const grandchildrenTree = this.buildHierarchyTree(
        child.label_id,
        courseId,
        versionId,
        allEntries,
        level + 1
      );

      if (grandchildrenTree) {
        lines.push(grandchildrenTree);
      }
    });

    return lines.join('\n');
  }

  // Helper to find root parent with circular reference protection
  private findRootParent(parentLabelId: number, allEntries: any[], visited: Set<number> = new Set(), depth: number = 0): any {
    // Safety checks: prevent infinite recursion
    const MAX_DEPTH = 100;
    if (depth > MAX_DEPTH) {
      console.warn(`Max depth exceeded while finding root parent for labelId: ${parentLabelId}`);
      return null;
    }

    if (visited.has(parentLabelId)) {
      console.warn(`Circular reference detected for labelId: ${parentLabelId}`);
      return null;
    }

    visited.add(parentLabelId);

    const parentEntry = allEntries.find(e => e.newv?.label_id === parentLabelId);
    
    if (!parentEntry) {
      return null;
    }

    if (parentEntry.newv.parent_label_id === 0 || parentEntry.newv.parent_label_id === null) {
      return parentEntry.newv;
    }

    // Recursively find the root with visited set and depth tracking
    return this.findRootParent(parentEntry.newv.parent_label_id, allEntries, visited, depth + 1);
  }

  // Helper to format datetime
  private formatDateTime(dateTime: any): string {
    try {
      const m = moment(dateTime);
      if (m.isValid()) {
        return m.format('M/D/YYYY, h:mm:ss A');
      }
    } catch (e) {
      // keep raw value
    }
    return dateTime;
  }

  // Find ALL child labels including historical ones
  findAllChildLabels(parentLabelId: number, courseId: number, versionId: number, allEntries: any[]): any[] {
    const children: any[] = [];
    const seenIds = new Set<number>();

    allEntries.forEach(entry => {
      if (entry.newv && 
          entry.newv.parent_label_id === parentLabelId &&
          entry.newv.course_id === courseId &&
          entry.newv.version_id === versionId) {
        
        const labelId = entry.newv.label_id;
        if (!seenIds.has(labelId)) {
          children.push({
            label_name: entry.newv.label_name,
            label_index: entry.newv.label_index,
            label_id: labelId
          });
          seenIds.add(labelId);
        }
      }
    });

    return children;
  }

  // Find child labels for a given parent label
  findChildLabels(parentLabelId: number, courseId: number, versionId: number, allEntries: any[]): any[] {
    const children: any[] = [];
    const seenIds = new Set<number>(); // Track seen label IDs to avoid duplicates

    allEntries.forEach(entry => {
      if (entry.newv && 
          entry.newv.parent_label_id === parentLabelId &&
          entry.newv.course_id === courseId &&
          entry.newv.version_id === versionId) {
        
        const labelId = entry.newv.label_id;
        if (!seenIds.has(labelId)) {
          children.push({
            label_name: entry.newv.label_name,
            label_index: entry.newv.label_index,
            label_id: labelId
          });
          seenIds.add(labelId);
        }
      }
    });

    // Sort children by label_index
    children.sort((a, b) => (a.label_index || 0) - (b.label_index || 0));
    
    return children;
  }
   private addEmailField(constructedContent: any[], element: any): any[] {
  // Disabled - do not add contact_email_id field
  // This field is not needed in revision history when it hasn't changed
  return constructedContent;
}

  getContentMapping(element: any, previousModifiedDate?: any) {
    const keyName = this.findChangedKeys(element?.oldv, element?.newv);
    let constructedContent = [];
    
    const MEANINGFUL_ID_FIELDS = ['version_id', 'parent_question_id', 'label_id', 'parent_label_id', 'course_id'];
    if (this.parentUrl === 'label') {
      const labelFields = ['label_id', 'label_name', 'label_index', 'parent_label_id', 'is_active'];
      const fieldsToHideInUpdates = ['label_index', 'is_active', 'parent_label_id'];
      const formatFieldName = (fieldName: string): string => {
        const nameMap = {
          'label_name': 'Label Name',
          'labelName': 'Label Name',
          'label_index': 'Label Index',
          'labelIndex': 'Label Index',
          'label_id': 'Label ID',
          'labelId': 'Label ID',
          'parent_label_id': 'Parent Label ID',
          'parentLabelId': 'Parent Label ID',
          'is_active': 'Is Active',
          'isActive': 'Is Active'
        };
        return nameMap[fieldName] || fieldName;
      };
      const formatFieldValue = (value: any): string => {
        if (value === null || value === undefined) return 'N/A';
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        return String(value).trim();
      };

      const normalizeForComparison = (value: any): any => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'boolean') return value ? 'yes' : 'no';
        return String(value).toLowerCase().trim();
      };
      const valuesAreDifferent = (oldVal: any, newVal: any): boolean => {
        const normalizedOld = normalizeForComparison(oldVal);
        const normalizedNew = normalizeForComparison(newVal);
        return normalizedOld !== normalizedNew;
      };
      const extractChangedFieldsFromArrays = (oldArray: any[], newArray: any[]) => {
        if (!Array.isArray(oldArray) || !Array.isArray(newArray)) {
          return { oldValue: '', newValue: '' };
        }
        const oldMap = new Map();
        const newMap = new Map();

        oldArray.forEach((item, idx) => {
          const key = item?.labelId || item?.id || idx;
          oldMap.set(key, item);
        });

        newArray.forEach((item, idx) => {
          const key = item?.labelId || item?.id || idx;
          newMap.set(key, item);
        });

        const oldChanges: string[] = [];
        const newChanges: string[] = [];
        const allKeys = new Set([...oldMap.keys(), ...newMap.keys()]);

        allKeys.forEach(key => {
          const oldItem = oldMap.get(key);
          const newItem = newMap.get(key);

          if (oldItem && newItem) {
            const allFields = new Set([
              ...Object.keys(oldItem || {}),
              ...Object.keys(newItem || {})
            ]);

            allFields.forEach(field => {
              if (fieldsToHideInUpdates.includes(field)) return;

              const oldVal = oldItem?.[field];
              const newVal = newItem?.[field];

              if (valuesAreDifferent(oldVal, newVal)) {
                oldChanges.push(String(oldVal));
                newChanges.push(String(newVal));
              }
            });
          }
        });

        return {
          oldValue: oldChanges.join('\n') || 'N/A',
          newValue: newChanges.join('\n') || 'N/A'
        };
      };
      const oldvIsArray = Array.isArray(element.oldv);
      const newvIsArray = Array.isArray(element.newv);

      if (oldvIsArray && newvIsArray) {
        const { oldValue, newValue } = extractChangedFieldsFromArrays(element.oldv, element.newv);
        
        if (oldValue !== 'N/A' || newValue !== 'N/A') {
          constructedContent.push({
            label: 'Labels',
            oldValue: oldValue,
            newValue: newValue
          });
        }
      } else {
        const labelFieldsInChange = keyName.filter(k => labelFields.includes(k));
        
        if (labelFieldsInChange.length > 0) {
          labelFieldsInChange.forEach(fieldName => {
            if (fieldsToHideInUpdates.includes(fieldName)) {
              return;
            }

            const oldValue = element.oldv?.[fieldName];
            const newValue = element.newv?.[fieldName];
            if (valuesAreDifferent(oldValue, newValue)) {
              constructedContent.push({
                label: formatFieldName(fieldName),
                oldValue: formatFieldValue(oldValue),
                newValue: formatFieldValue(newValue)
              });
            }
          });
        }
      }
      const formatDateTime = (dateTime: any): string => {
        if (!dateTime) return 'N/A';
        try {
          const m = moment(dateTime);
          if (m.isValid()) {
            return m.format("M/D/YYYY, h:mm:ss A");
          }
        } catch (e) {}
        return String(dateTime);
      };
      constructedContent.push({
        label: 'Modified Date',
        oldValue: previousModifiedDate ? formatDateTime(previousModifiedDate) : 'N/A',
        newValue: formatDateTime(element.modified_date)
      });
      if (constructedContent.length > 0) {
        constructedContent = this.addEmailField(constructedContent, element);
      }
      return constructedContent.length ? constructedContent : null;
    }

    if (this.parentUrl === 'tags') {
      const TAG_SKIP_FIELDS = new Set([
        'id', 'organization_id', 'created_user_id', 'modified_user_id',
        'created_datetime', 'is_in_use', 'modified_user_name'
      ]);
      keyName.forEach((key) => {
        if (TAG_SKIP_FIELDS.has(key)) return;
        if (this.areValuesEffectivelySame(element.oldv[key], element.newv[key])) return;
        if (key === 'modified_datetime') {
          const fmt = (v: any) => v ? moment(v).format('M/D/YYYY, h:mm:ss A') : '';
          constructedContent.push({
            label: 'Modified Time',
            oldValue: fmt(element.oldv[key]),
            newValue: fmt(element.newv[key])
          });
          return;
        }
        constructedContent.push({
          label: this.util.capitalizeFirstLetter(key.split('_').join(' ')),
          oldValue: `${key}: ${element.oldv[key] ?? ''}`,
          newValue: `${key}: ${element.newv[key] ?? ''}`
        });
      });
      if (constructedContent.length > 0) {
        constructedContent = this.addEmailField(constructedContent, element);
      }
      return constructedContent.length ? constructedContent : null;
    }

    keyName.forEach((key) => {
      // Enhanced filtering to hide ID fields and internal fields
      // but allow meaningful IDs like version_id, course_id, parent_question_id
       if (this.parentUrl === 'apiIntegration') {
        if (key === 'modified_by' || key === 'modified_date') {
          return;
        }
      }

      // Skip internal fields for EvaluationCriteria
      if (this.parentUrl === 'EvaluationCriteria' || element?.audit_table_name === 'question_paper_evaluation_criteria') {
        const EC_INTERNAL_FIELDS = ['id', 'organization_id', 'created_user_id', 'modified_user_id',
          'created_datetime', 'modified_datetime', 'question_paper_version_id', 'is_deleted', 'is_archived',
          'is_active', 'organization_name', 'created_user_name', 'created_user_username',
          'course_name', 'course_version_name'];
        if (EC_INTERNAL_FIELDS.includes(key)) return;
      }

      // Skip internal/embedded fields for QPV — labels hierarchy is embedded inside category display
      if (this.parentUrl === 'questionPaperVersions') {
        const QPV_SKIP_FIELDS = ['id', 'organization_id', 'created_user_id', 'modified_user_id',
          'created_datetime', 'modified_datetime', 'is_deleted', 'is_archived',
          'question_paper_id', 'labels'];
        if (QPV_SKIP_FIELDS.includes(key)) return;
      }

      // Skip internal fields for labels
      if (this.parentUrl === 'label') {
        if (key === 'id' || key === 'created_user_id' || key === 'is_deleted' || key === 'organization_id' || key === 'created_datetime' || key === 'modified_user_id') {
          return;
        }
        const labelFields = ['label_id', 'label_name', 'label_index', 'parent_label_id', 'is_active', 'modified_datetime'];
        if (labelFields.includes(key)) {
          return;
        }
      }

      if (this.shouldHideDuplicateAuditField(key)) {
        return;
      }
      
      const isOpportunityModule = this.parentUrl === 'Opportunities' || this.parentUrl === 'Deals';
      const isOpportunityStatusField = isOpportunityModule && (key === 'type_id' || key === 'status_id');

      const isIdField = !isOpportunityStatusField && (
        key === 'id' ||
        (key.endsWith('_id') && !MEANINGFUL_ID_FIELDS.includes(key)) ||
        (key.endsWith('_ids') && !MEANINGFUL_ID_FIELDS.includes(key)) ||
        key === 'created_datetime' ||
        key === 'modified_datetime' ||
        key === 'created_by' ||
        key === 'created_by_id' ||
        key === 'modified_user_name' ||
        key === 'contact_email_id' ||
        key === 'version_name' || // Hide version_name since we display version_id instead
        this.unlistedKeys.includes(key));

      if (!isIdField) {
        // API Integration - ignore auto generated header changes
        if (key === 'headers' && this.parentUrl === 'apiIntegration') {

          const oldHeaders = element.oldv?.headers || {};
          const newHeaders = element.newv?.headers || {};

          const oldStr = JSON.stringify(oldHeaders);
          const newStr = JSON.stringify(newHeaders);

          // same headers → skip
          if (oldStr === newStr) {
            return;
          }

          // if only token/hash regenerated but keys same → skip
          const oldKeys = Object.keys(oldHeaders).sort();
          const newKeys = Object.keys(newHeaders).sort();

          if (JSON.stringify(oldKeys) === JSON.stringify(newKeys)) {

            const systemHeaders = ['Accept','Authorization','User-Agent'];

            const onlySystemHeaders =
              oldKeys.every(k => systemHeaders.includes(k)) &&
              newKeys.every(k => systemHeaders.includes(k));

            if (onlySystemHeaders) {
              return;
            }
          }
}    // First check if values are actually different - MOST IMPORTANT
        if (this.areValuesEffectivelySame(element.oldv[key], element.newv[key])) {
          return; // Skip this field if values are same
        }

        // Special handling for UserCategory custom_attrs
        if (key === 'custom_attrs' && this.parentUrl === 'UserCategory') {
          // Check if the LAST attribute has newCustomAttribute: true
          const hasNewCustomAttribute = element.newv[key] &&
            Array.isArray(element.newv[key]) &&
            element.newv[key].length > 0 &&
            element.newv[key][element.newv[key].length - 1].newCustomAttribute === true;

          // If the LAST attribute has newCustomAttribute: true, completely ignore this field
          if (hasNewCustomAttribute) {
            return; // Skip showing any custom attributes updates
          }

          // Only show updates if the LAST attribute has newCustomAttribute: false
          const hasUpdatedAttributes = element.newv[key] &&
            Array.isArray(element.newv[key]) &&
            element.newv[key].length > 0 &&
            element.newv[key][element.newv[key].length - 1].newCustomAttribute === false;

          if (hasUpdatedAttributes) {
            // Find the changed attribute by comparing oldv and newv arrays
            const changedAttribute = this.findChangedCustomAttributeByComparison(element.oldv[key], element.newv[key]);

            if (changedAttribute) {
              constructedContent.push({
                label: this.getLabelName(key, element),
                oldValue: this.formatCustomAttributesForUpdate(changedAttribute.old ? [changedAttribute.old] : []),
                newValue: this.formatCustomAttributesForUpdate([changedAttribute.new])
              });
            }
          }

          return;
        }

        // Hide Modified Date&Time updates when they are created for custom attributes with newCustomAttribute: true
        if (key === 'modified_datetime' &&
          this.parentUrl === 'UserCategory' &&
          element.newv?.custom_attrs &&
          Array.isArray(element.newv.custom_attrs) &&
          element.newv.custom_attrs.length > 0 &&
          element.newv.custom_attrs[element.newv.custom_attrs.length - 1].newCustomAttribute === true) {
          return; // Skip showing Modified Date&Time when the last custom attribute has newCustomAttribute: true
        }
        if (key === 'perm_levels') {
          // Special handling for permission data
          const permissionChanges = this.formatPermissionChanges(
            element.oldv[key],
            element.newv[key]
          );
          constructedContent.push(...permissionChanges);
        }
        else if (key === 'version_id') {
          // Special handling for version_id - show version name instead of ID
          constructedContent.push({
            label: 'Version',
            oldValue: element.oldv.version_name || element.oldv.version_id,
            newValue: element.newv.version_name || element.newv.version_id
          });
        }
        else if (key === 'spoc_ids') {
          constructedContent.push({
            label: 'SPOCs',
            oldValue: element.oldv.spoc_names || element.spoc_names || (Array.isArray(element.oldv.spoc_ids) ? element.oldv.spoc_ids.join(', ') : element.oldv.spoc_ids),
            newValue: element.newv.spoc_names || element.spoc_names || (Array.isArray(element.newv.spoc_ids) ? element.newv.spoc_ids.join(', ') : element.newv.spoc_ids)
          });
        }
        else if (key === 'custom_attrs') {
          // Regular custom attributes handling for other modules
          constructedContent.push({
            label: this.getLabelName(key, element),
            oldValue: this.formatCustomAttributes(element.oldv[key]),
            newValue: this.formatCustomAttributes(element.newv[key])
          });
        }
        else if (key === 'labels' && this.parentUrl === 'questions') {
          // Format labels as tree structure for questions
          const buildLabelTree = (labels: any[], parentId: any): string => {
            const children = labels.filter((l: any) => {
              const pid = l.parentLabelId !== undefined ? l.parentLabelId : l.parent_label_id;
              return pid === parentId;
            });
            if (!children.length) return '';
            return children.map((child: any) => {
              const name = child.labelName || child.label_name || '';
              const childId = child.labelId !== undefined ? child.labelId : child.label_id;
              const grandchildren = buildLabelTree(labels, childId);
              if (grandchildren) {
                return `<details style="margin-left:20px"><summary style="cursor:pointer;padding:2px 0">&#9675; ${name}</summary><div>${grandchildren}</div></details>`;
              }
              return `<div style="margin-left:20px;padding:2px 0">&#9675; ${name}</div>`;
            }).join('');
          };

          const formatLabelsForDisplay = (labelsData: any): string => {
            if (!labelsData) return 'No labels';
            try {
              let parsed = labelsData;
              if (typeof labelsData === 'string') {
                parsed = JSON.parse(labelsData);
              }
              if (!Array.isArray(parsed) || parsed.length === 0) return 'No labels';

              const groupLines: string[] = [];
              parsed.forEach((group: any) => {
                const labels = Array.isArray(group.labels) ? group.labels : [];
                if (!labels.length) return;
                const rootLabel = labels.find((l: any) => {
                  const pid = l.parentLabelId !== undefined ? l.parentLabelId : l.parent_label_id;
                  return pid === null || pid === undefined || l.level === 0;
                });
                if (rootLabel) {
                  const rootId = rootLabel.labelId !== undefined ? rootLabel.labelId : rootLabel.label_id;
                  const name = rootLabel.labelName || rootLabel.label_name || '';
                  const subtree = buildLabelTree(labels, rootId);
                  if (subtree) {
                    groupLines.push(`<details style="margin-bottom:4px"><summary style="cursor:pointer;padding:2px 0;font-weight:500">&#9675; ${name}</summary><div>${subtree}</div></details>`);
                  } else {
                    groupLines.push(`<div style="padding:2px 0;font-weight:500">&#9675; ${name}</div>`);
                  }
                }
              });

              return groupLines.length ? groupLines.join('') : 'No labels';
            } catch (e) {
              return String(labelsData);
            }
          };

          constructedContent.push({
            label: 'Labels',
            oldValue: formatLabelsForDisplay(element.oldv[key]),
            newValue: formatLabelsForDisplay(element.newv[key]),
          });
        }
        else if (key === 'tags' && (this.parentUrl === 'EvaluationCriteria' || element.audit_table_name === 'question_paper_evaluation_criteria')) {
          const parse = (v: any) => {
            if (!v) return [];
            try { return typeof v === 'string' ? JSON.parse(v) : (Array.isArray(v) ? v : []); } catch (e) { return []; }
          };
          const oldTags = parse(element.oldv[key]);
          const newTags = parse(element.newv[key]);

          const valueOf = (t: any) => (t && t.key && t[t.key] != null) ? t[t.key] : '';
          const idOf = (t: any) => `${t?.key || ''}::${valueOf(t)}`;
          const fmt = (t: any) => `Tag: ${t?.key || ''}<br>Value: ${valueOf(t)}<br>Type: ${t?.type || ''}`;

          const oldMap = new Map<string, any>();
          oldTags.forEach((t: any) => oldMap.set(idOf(t), t));
          const newMap = new Map<string, any>();
          newTags.forEach((t: any) => newMap.set(idOf(t), t));

          const added: string[] = [];
          const removed: string[] = [];
          const modifiedOld: string[] = [];
          const modifiedNew: string[] = [];

          const allIds = new Set<string>([...oldMap.keys(), ...newMap.keys()]);
          allIds.forEach(tid => {
            const o = oldMap.get(tid);
            const n = newMap.get(tid);
            if (o && !n) removed.push(fmt(o));
            else if (!o && n) added.push(fmt(n));
            else if (o && n && (o.type || '') !== (n.type || '')) {
              modifiedOld.push(fmt(o));
              modifiedNew.push(fmt(n));
            }
          });

          const SEP = '<br><br>';
          if (added.length) constructedContent.push({ label: 'Added Tags', oldValue: '', newValue: added.join(SEP) });
          if (removed.length) constructedContent.push({ label: 'Removed Tags', oldValue: removed.join(SEP), newValue: '' });
          if (modifiedOld.length) constructedContent.push({ label: 'Modified Tags', oldValue: modifiedOld.join(SEP), newValue: modifiedNew.join(SEP) });
        }
        else if (key === 'tags' && this.parentUrl === 'questions') {
          const parse = (v: any): any[] => {
            if (!v) return [];
            try { return typeof v === 'string' ? JSON.parse(v) : (Array.isArray(v) ? v : []); } catch (e) { return []; }
          };
          const oldTags = parse(element.oldv[key]);
          const newTags = parse(element.newv[key]);

          // Resolve display name: prefer tag map lookup by id, then name/key fields
          const tagDisplayName = (t: any): string => {
            if (t?.id != null && this.tagMap.has(Number(t.id))) {
              return this.tagMap.get(Number(t.id)) ?? (t?.name || t?.key || String(t.id));
            }
            return t?.name || t?.key || String(t?.id ?? '');
          };
          const tagId = (t: any): string => String(t?.id ?? JSON.stringify(t));

          // Compare only by ID to avoid false positives from differing name/key fields
          const oldIds = new Set(oldTags.map(tagId));
          const newIds = new Set(newTags.map(tagId));
          const sameIds = [...oldIds].every(id => newIds.has(id)) && [...newIds].every(id => oldIds.has(id));
          if (sameIds) return; // No real tag change — skip entirely

          const oldMap = new Map<string, string>();
          oldTags.forEach((t: any) => oldMap.set(tagId(t), tagDisplayName(t)));
          const newMap = new Map<string, string>();
          newTags.forEach((t: any) => newMap.set(tagId(t), tagDisplayName(t)));

          const added: string[] = [];
          const removed: string[] = [];

          newMap.forEach((name, k) => { if (!oldMap.has(k)) added.push(name); });
          oldMap.forEach((name, k) => { if (!newMap.has(k)) removed.push(name); });

          // Show old tag → new tag as a single Tags row when one tag replaced another
          if (removed.length > 0 || added.length > 0) {
            constructedContent.push({
              label: 'Tags',
              oldValue: removed.length ? removed.join(', ') : (oldTags.length ? oldTags.map(tagDisplayName).join(', ') : ''),
              newValue: added.length ? added.join(', ') : (newTags.length ? newTags.map(tagDisplayName).join(', ') : '')
            });
          }
        }
        else if (key === 'evaluation_criteria' && (this.parentUrl === 'EvaluationCriteria' || element.audit_table_name === 'question_paper_evaluation_criteria')) {
          const parse = (v: any) => {
            if (!v) return {};
            try { return typeof v === 'string' ? JSON.parse(v) : v; } catch (e) { return {}; }
          };
          const oldEC = parse(element.oldv[key]);
          const newEC = parse(element.newv[key]);

          // Format a single evaluation parameter row
          const fmtParam = (paramName: string, rows: any[]): string => {
            if (!rows || !rows.length) return `Parameter: ${paramName}`;
            return rows.map(r => `Parameter: ${paramName}<br>Type: ${r.item || '—'}<br>Min Pass %: ${r.value ?? '—'}`).join('<br>');
          };

          const added: string[] = [];
          const removed: string[] = [];
          const modifiedOld: string[] = [];
          const modifiedNew: string[] = [];

          const allParams = new Set([...Object.keys(oldEC), ...Object.keys(newEC)]);
          allParams.forEach(param => {
            const oldRows = oldEC[param];
            const newRows = newEC[param];
            const oldStr = JSON.stringify(oldRows ?? null);
            const newStr = JSON.stringify(newRows ?? null);
            if (oldRows !== undefined && newRows === undefined) {
              removed.push(fmtParam(param, oldRows));
            } else if (oldRows === undefined && newRows !== undefined) {
              added.push(fmtParam(param, newRows));
            } else if (oldStr !== newStr) {
              modifiedOld.push(fmtParam(param, oldRows));
              modifiedNew.push(fmtParam(param, newRows));
            }
          });

          const SEP = '<br><br>';
          const oldParts = [...removed, ...modifiedOld];
          const newParts = [...added, ...modifiedNew];
          if (oldParts.length || newParts.length) {
            constructedContent.push({
              label: 'Evaluation Parameters',
              oldValue: oldParts.join(SEP) || 'No data',
              newValue: newParts.join(SEP) || 'No data',
              isHtml: true
            });
          }
        }
        else if (key === 'count_of_question_overlap' && this.parentUrl === 'questionPaperVersions') {
          
          const fmtOverlap = (v: any) => (Number(v) > 0 ? 'Yes' : 'No');
          constructedContent.push({
            label: 'Question Overlap Allowed',
            oldValue: `Question Overlap Allowed : <br>${fmtOverlap(element.oldv[key])}`,
            newValue: `Question Overlap Allowed : <br>${fmtOverlap(element.newv[key])}`,
            isHtml: true
          });
        }
         else if (key === 'label' && this.parentUrl === 'questionPaperVersions') {
          const buildLabelTreeById = (labels: any[], parentUniqueId: any): string => {
            const children = labels.filter((l: any) => l.__parentUniqueId === parentUniqueId);
            if (!children.length) return '';
            return children.map((child: any) => {
              const name = child.labelName || child.label_name || '';
              const grandchildren = buildLabelTreeById(labels, child.__uniqueId);
              if (grandchildren) {
                return `<details style="margin-left:20px"><summary style="cursor:pointer;padding:2px 0">&#9675; ${name}</summary><div>${grandchildren}</div></details>`;
              }
              return `<div style="margin-left:20px;padding:2px 0">&#9675; ${name}</div>`;
            }).join('');
          };

          const formatLabelsForDisplay = (labelsData: any): string => {
            if (!labelsData) return 'No labels';
            try {
              let parsed = labelsData;
              if (typeof labelsData === 'string') {
                parsed = JSON.parse(labelsData);
              }
              if (!Array.isArray(parsed) || parsed.length === 0) return 'No labels';

       
              const byUniqueId = new Map<any, any>();
              parsed.forEach((group: any) => {
                const groupLabels = Array.isArray(group.labels) ? group.labels : [];
                const labelIdToUniqueId = new Map<any, any>();
                groupLabels.forEach((l: any) => {
                  const lid = l.labelId !== undefined ? l.labelId : l.label_id;
                  const uid = l.id;
                  if (uid != null && lid != null) labelIdToUniqueId.set(lid, uid);
                });
                groupLabels.forEach((l: any) => {
                  const uid = l.id;
                  if (uid == null) return;
                  const pLabelId = l.parentLabelId !== undefined ? l.parentLabelId : l.parent_label_id;
                  const parentUniqueId = (pLabelId !== null && pLabelId !== undefined)
                    ? (labelIdToUniqueId.get(pLabelId) ?? null)
                    : null;
                  const existing = byUniqueId.get(uid);
                  if (!existing || parentUniqueId != null) {
                    byUniqueId.set(uid, {
                      ...l,
                      __uniqueId: uid,
                      __parentUniqueId: parentUniqueId,
                    });
                  }
                });
              });

              const allLabels = Array.from(byUniqueId.values());
              const roots = allLabels.filter((l: any) => l.__parentUniqueId == null);

              const groupLines: string[] = [];
              roots.forEach((rootLabel: any) => {
                const name = rootLabel.labelName || rootLabel.label_name || '';
                const subtree = buildLabelTreeById(allLabels, rootLabel.__uniqueId);
                if (subtree) {
                  groupLines.push(`<details style="margin-bottom:4px"><summary style="cursor:pointer;padding:2px 0;font-weight:500">&#9675; ${name}</summary><div>${subtree}</div></details>`);
                } else {
                  groupLines.push(`<div style="padding:2px 0;font-weight:500">&#9675; ${name}</div>`);
                }
              });

              return groupLines.length ? groupLines.join('') : 'No labels';
            } catch (e) {
              return String(labelsData);
            }
          };

          const oldDisplay = formatLabelsForDisplay(element.oldv[key]);
          const newDisplay = formatLabelsForDisplay(element.newv[key]);
          if (oldDisplay !== newDisplay) {
            constructedContent.push({
              label: 'QP Label',
              oldValue: oldDisplay,
              newValue: newDisplay,
              isHtml: true
            });
          }
        }
        else if (key === 'count_of_question_overlap' && this.parentUrl === 'questionPaperVersions') {
          // Numeric slider (0 = no overlap) — needs Yes/No conversion, so it keeps its
          // own branch instead of going through the generic catch-all below.
          const fmtOverlap = (v: any) => (Number(v) > 0 ? 'Yes' : 'No');
          constructedContent.push({
            label: 'Question Overlap Allowed',
            oldValue: `Question Overlap Allowed : <br>${fmtOverlap(element.oldv[key])}`,
            newValue: `Question Overlap Allowed : <br>${fmtOverlap(element.newv[key])}`,
            isHtml: true
          });
        }
        else if (key === 'category' && this.parentUrl === 'questionPaperVersions') {
          const parse = (v: any) => {
            if (!v) return {};
            try { return typeof v === 'string' ? JSON.parse(v) : v; } catch (e) { return {}; }
          };
          const parseArr = (v: any): any[] => {
            if (!v) return [];
            try { return typeof v === 'string' ? JSON.parse(v) : (Array.isArray(v) ? v : []); } catch (e) { return []; }
          };
          const oldCat = parse(element.oldv[key]);
          const newCat = parse(element.newv[key]);

          // Build a label tree (same style as question revision history)
          const buildLabelTree = (labelIds: number[], labels: any[]): string => {
            // Flatten all labels from groups
            const allLabels: any[] = [];
            labels.forEach((group: any) => {
              const groupLabels = Array.isArray(group.labels) ? group.labels : [];
              groupLabels.forEach((l: any) => allLabels.push(l));
            });

            // Only keep labels whose id is in labelIds
            const selected = allLabels.filter((l: any) =>
              labelIds.includes(l.labelId ?? l.id)
            );

            if (!selected.length) return 'No labels';

            // Build tree recursively
            const buildTree = (parentId: any): string => {
              const children = selected.filter((l: any) => {
                const pid = l.parentLabelId !== undefined ? l.parentLabelId : l.parent_label_id;
                return pid === parentId;
              });
              if (!children.length) return '';
              return children.map((child: any) => {
                const name = child.labelName || child.label_name || '';
                const childId = child.labelId !== undefined ? child.labelId : child.id;
                const subtree = buildTree(childId);
                if (subtree) {
                  return `<details style="margin-left:20px"><summary style="cursor:pointer;padding:2px 0">&#9675; ${name}</summary><div>${subtree}</div></details>`;
                }
                return `<div style="margin-left:20px;padding:2px 0">&#9675; ${name}</div>`;
              }).join('');
            };

            // Find root labels (parentLabelId === 0 or null)
            const roots = selected.filter((l: any) => {
              const pid = l.parentLabelId !== undefined ? l.parentLabelId : l.parent_label_id;
              return pid === null || pid === undefined || pid === 0;
            });

            if (!roots.length) {
              // No root found — just list all flat
              return selected.map((l: any) =>
                `<div style="padding:2px 0">&#9675; ${l.labelName || l.label_name || ''}</div>`
              ).join('');
            }

            return roots.map((root: any) => {
              const rootId = root.labelId !== undefined ? root.labelId : root.id;
              const name = root.labelName || root.label_name || '';
              const subtree = buildTree(rootId);
              if (subtree) {
                return `<details style="margin-bottom:4px"><summary style="cursor:pointer;padding:2px 0;font-weight:500">&#9675; ${name}</summary><div>${subtree}</div></details>`;
              }
              return `<div style="padding:2px 0;font-weight:500">&#9675; ${name}</div>`;
            }).join('');
          };

          // Flatten category into a list of entries with a unique key for diffing
          const flattenCat = (cat: any): { key: string; evalMode: string; diffLevel: string; entry: any }[] => {
            const result: { key: string; evalMode: string; diffLevel: string; entry: any }[] = [];
            if (!cat || typeof cat !== 'object') return result;
            Object.keys(cat).forEach(evalMode => {
              const levelMap = cat[evalMode];
              if (!levelMap || typeof levelMap !== 'object') return;
              Object.keys(levelMap).forEach(diffLevel => {
                const entries = levelMap[diffLevel];
                if (!Array.isArray(entries)) return;
                entries.forEach((entry: any) => {
                  const labelIds: number[] = Array.isArray(entry.labelId) ? entry.labelId : [];
                  const key = `${evalMode}||${diffLevel}||${JSON.stringify(labelIds.slice().sort())}`;
                  result.push({ key, evalMode, diffLevel, entry });
                });
              });
            });
            return result;
          };

          // Format a single flat entry into readable HTML
          const fmtEntry = (evalMode: string, diffLevel: string, entry: any, labels: any[]): string => {
            const labelIds: number[] = Array.isArray(entry.labelId) ? entry.labelId : [];
            let labelTree = 'No labels';

            if (labels.length) {
              const built = buildLabelTree(labelIds, labels);
              if (built && built !== 'No labels') {
                labelTree = built;
              }
            }

            // Fallback: build a <details>/<summary> list from entry.labelName
            if (labelTree === 'No labels' && Array.isArray(entry.labelName) && entry.labelName.filter(Boolean).length) {
              const names = entry.labelName.filter(Boolean) as string[];
              if (names.length === 1) {
                labelTree = `<div style="padding:2px 0;font-weight:500">&#9675; ${names[0]}</div>`;
              } else {
                // Show all names as collapsible items (siblings, not parent-child)
                const items = names.map((n: string) =>
                  `<div style="padding:2px 0">&#9675; ${n}</div>`
                ).join('');
                labelTree = `<details style="margin-bottom:4px"><summary style="cursor:pointer;padding:2px 0;font-weight:500">Labels (${names.length})</summary><div style="margin-left:16px">${items}</div></details>`;
              }
            }

            return (
              `Evaluation Mode: ${evalMode}<br>` +
              `Difficulty Level: ${diffLevel}<br>` +
              `Marks: ${entry.nos ?? entry.marks ?? '—'}<br>` +
              `Label:<br><div style="margin-left:16px">${labelTree}</div>`
            );
          };

          const oldStr = JSON.stringify(oldCat);
          const newStr = JSON.stringify(newCat);
          if (oldStr !== newStr) {
            const oldLabels = parseArr(element.oldv['labels'] ?? element.oldv['label']);
            const newLabels = parseArr(element.newv['labels'] ?? element.newv['label']);

            const oldFlat = flattenCat(oldCat);
            const newFlat = flattenCat(newCat);

            const oldKeys = new Set(oldFlat.map(e => e.key));
            const newKeys = new Set(newFlat.map(e => e.key));

            // Entries only in old = removed; entries only in new = added; entries in both = check marks changed
            const removedEntries = oldFlat.filter(e => !newKeys.has(e.key));
            const addedEntries = newFlat.filter(e => !oldKeys.has(e.key));

            // For entries present in both, check if marks (nos) changed
            const changedOld: typeof oldFlat = [];
            const changedNew: typeof newFlat = [];
            oldFlat.forEach(oldEntry => {
              const match = newFlat.find(ne => ne.key === oldEntry.key);
              if (match) {
                const oldMarks = oldEntry.entry.nos ?? oldEntry.entry.marks;
                const newMarks = match.entry.nos ?? match.entry.marks;
                if (String(oldMarks) !== String(newMarks)) {
                  changedOld.push(oldEntry);
                  changedNew.push(match);
                }
              }
            });

            const oldParts = [...removedEntries, ...changedOld]
              .map(e => fmtEntry(e.evalMode, e.diffLevel, e.entry, oldLabels));
            const newParts = [...addedEntries, ...changedNew]
              .map(e => fmtEntry(e.evalMode, e.diffLevel, e.entry, newLabels));

            if (oldParts.length || newParts.length) {
              constructedContent.push({
                label: 'Assign Marks',
                oldValue: oldParts.join('<br><br>') || 'No data',
                newValue: newParts.join('<br><br>') || 'No data',
                isHtml: true
              });
            }
          }
        }
        else if ((key === 'grouping_criteria' || key === 'question_grouping_logic') && this.parentUrl === 'questionPaperVersions') {
          const parse = (v: any) => {
            if (!v) return {};
            try { return typeof v === 'string' ? JSON.parse(v) : v; } catch (e) { return {}; }
          };
          const oldGC = parse(element.oldv[key]);
          const newGC = parse(element.newv[key]);

          const fmtGrouping = (gc: any): string => {
            if (!gc || typeof gc !== 'object') return '';
            return Object.keys(gc)
              .map(evalMode => `Evaluation Mode: ${evalMode}<br>Grouping Criteria: ${gc[evalMode] || '—'}`)
              .join('<br><br>');
          };

          const oldStr = JSON.stringify(oldGC);
          const newStr = JSON.stringify(newGC);
          if (oldStr !== newStr) {
            constructedContent.push({
              label: 'Assign Grouping Criteria',
              oldValue: fmtGrouping(oldGC) || 'No data',
              newValue: fmtGrouping(newGC) || 'No data',
              isHtml: true
            });
          }
        }
        else if (key === 'grading_criteria') {
          const parse = (v: any) => {
            if (!v) return {};
            try { return typeof v === 'string' ? JSON.parse(v) : v; } catch (e) { return {}; }
          };
          const oldGrades = parse(element.oldv[key]);
          const newGrades = parse(element.newv[key]);
          const fmtRange = (grade: string, g: any) => g ? `Grade: ${grade}<br>Minimum Mark: ${g.min ?? ''}<br>Maximum Marks: ${g.max ?? ''}` : '';

          const added: string[] = [];
          const removed: string[] = [];
          const modifiedOld: string[] = [];
          const modifiedNew: string[] = [];

          const allKeys = new Set([...Object.keys(oldGrades), ...Object.keys(newGrades)]);
          allKeys.forEach(g => {
            if (!g) return; // skip empty-string keys from placeholder rows
            const o = oldGrades[g];
            const n = newGrades[g];
            if (o && !n) {
              removed.push(fmtRange(g, o));
            } else if (!o && n) {
              added.push(fmtRange(g, n));
            } else if (o && n && (String(o.min) !== String(n.min) || String(o.max) !== String(n.max))) {
              modifiedOld.push(fmtRange(g, o));
              modifiedNew.push(fmtRange(g, n));
            }
          });

          const SEP = '<br><br>';
          if (added.length) {
            constructedContent.push({ label: 'Added Grades', oldValue: '', newValue: added.join(SEP) });
          }
          if (removed.length) {
            constructedContent.push({ label: 'Removed Grades', oldValue: removed.join(SEP), newValue: '' });
          }
          if (modifiedOld.length) {
            constructedContent.push({ label: 'Modified Grades', oldValue: modifiedOld.join(SEP), newValue: modifiedNew.join(SEP) });
          }
        }
        else if (key === 'tags' && this.parentUrl === 'assessmentPlan') {
          const extractProjectFromTags = (tags: any): string => {
            if (!tags) return null;
            try {
              const parsed = typeof tags === 'string' ? JSON.parse(tags) : tags;
              if (Array.isArray(parsed)) {
                const projectTag = parsed.find((t: any) => t.type === 'Project' || t.key === 'Project');
                if (projectTag) {
                  return projectTag['Project'] || projectTag.value || null;
                }
              }
            } catch (e) {}
            return null;
          };

          const oldProject = extractProjectFromTags(element.oldv[key]);
          const newProject = extractProjectFromTags(element.newv[key]);

          if (oldProject !== newProject) {
            constructedContent.push({
              label: 'Project',
              oldValue: oldProject || 'None',
              newValue: newProject || 'None'
            });
          }
          // Skip showing raw tags changes — only surface the Project change above
          return;
        }
        else if (key === 'is_active') {
          constructedContent.push({
            label: this.getLabelName(key, element),
            oldValue: element.oldv[key] ? 'Active' : 'Inactive',
            newValue: element.newv[key] ? 'Active' : 'Inactive'
          });
        }
        else if (key === 'group_candidates' && this.parentUrl === 'assessmentPlan') {
          const parseMeta = (m: any): any => {
            if (!m) return null;
            try { return typeof m === 'string' ? JSON.parse(m) : m; } catch (e) { return null; }
          };
          const oldMeta = parseMeta(element.oldv[key]);
          const newMeta = parseMeta(element.newv[key]);

          const formatCandidateMeta = (meta: any): string | null => {
            if (!meta) return null;
            const lines = [];
            if (meta.total !== undefined) lines.push(`Total no of candidates: ${meta.total}`);
            if (meta.published !== undefined) lines.push(`Published candidates: ${meta.published}`);
            if (meta.added !== undefined) lines.push(`Added candidates: ${meta.added}`);
            if (meta.removed !== undefined) lines.push(`Removed candidates: ${meta.removed}`);
            return lines.join('<br>');
          };

          constructedContent.push({
            label: 'Candidates',
            oldValue: formatCandidateMeta(oldMeta),
            newValue: formatCandidateMeta(newMeta),
            oldFileUrl: oldMeta?.file_url || null,
            newFileUrl: newMeta?.file_url || null
          });
        }
        else if ((key === 'candidate_meta' || key === 'candidateMeta') && this.parentUrl === 'assessmentPlan') {
          // Special handling for candidate metadata including file URL
          const parseMeta = (metaData: any): any => {
            if (!metaData) return null;
            try {
              if (typeof metaData === 'string') {
                return JSON.parse(metaData);
              }
              return metaData;
            } catch (e) {
              return null;
            }
          };

          const oldMeta = parseMeta(element.oldv[key]);
          const newMeta = parseMeta(element.newv[key]);

          // Keep full candidate metadata block for reference (with file URL inside)
          constructedContent.push({
            label: 'Candidate Details',
            oldValue: oldMeta ? JSON.stringify(oldMeta, null, 2) : 'N/A',
            newValue: newMeta ? JSON.stringify(newMeta, null, 2) : 'N/A',
            isCode: true,
            isFile: false
          });

          const oldFileUrl = oldMeta?.file_url || oldMeta?.fileUrl || '';
          const newFileUrl = newMeta?.file_url || newMeta?.fileUrl || '';

          if (oldFileUrl || newFileUrl) {
            constructedContent.push({
              label: 'File Url',
              oldValue: oldFileUrl ? oldFileUrl + '?response-content-disposition=attachment' : null,            
                newValue: newFileUrl ? newFileUrl + '?response-content-disposition=attachment' : null,
              isLink: true,
              isFile: false
            });
          }
        }
        else {
          const normalizeEmpty = (v: any) => {
            if (v === null || v === undefined) return null;
            if (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0) return null;
            if (Array.isArray(v) && v.length === 0) return null;
            return v;
          };
          const fieldLabel = this.getLabelName(key, element);

          if (this.parentUrl === 'questionPaperVersions') {
            const oldV = normalizeEmpty(element.oldv[key]);
            const newV = normalizeEmpty(element.newv[key]);
            constructedContent.push({
              label: fieldLabel,
              oldValue: `${fieldLabel} : <br>${oldV ?? ''}`,
              newValue: `${fieldLabel} : <br>${newV ?? ''}`,
              isHtml: true
            });
          } else {
            constructedContent.push({
              label: fieldLabel,
              oldValue: normalizeEmpty(element.oldv[key]),
              newValue: normalizeEmpty(element.newv[key])
            });
          }
        }
      }
    });
      // Only add email field if there are actual changes
      if (constructedContent.length > 0) {
        constructedContent = this.addEmailField(constructedContent, element);
      }

    return constructedContent.length ? constructedContent : null;
  }
  private formatCustomAttributes(attrs: any): string {
    if (!attrs || (Array.isArray(attrs) && attrs.length === 0)) {
      return 'None';
    }

    try {
      // Handle already formatted string with bullet points
      if (typeof attrs === 'string' && attrs.includes('•')) {
        return attrs.split('•')
          .filter(item => item.trim() !== '')
          .map(item => `• ${item.trim()}`)
          .join('\n');
      }

      // Parse JSON string if needed
      const parsedAttrs = typeof attrs === 'string' ? JSON.parse(attrs) : attrs;

      // Handle array of attribute objects
      if (Array.isArray(parsedAttrs)) {
        return parsedAttrs.map(attr => {
          // Handle simple string case
          if (typeof attr === 'string') {
            return `• ${attr}`;
          }

          // Handle attribute object case
          const attributeLines = [];
          // Add Sno if exists
          if (attr.sno !== undefined) {
            attributeLines.push(`• Sno: ${attr.sno}`);
          }
          // Add other properties
          for (const [key, value] of Object.entries(attr)) {
            if (key !== 'sno') {
              const formattedKey = this.util.capitalizeFirstLetter(key.split('_').join(' '));
              const formattedValue = value === null ? 'null' : value;
              attributeLines.push(`• ${formattedKey}: ${formattedValue}`);
            }
          }
          return attributeLines.join('\n\n'); // Add extra line between different attributes
        }).join('\n\n'); // Add extra line between different attributes
      }

      // Handle single object case
      if (typeof parsedAttrs === 'object') {
        return Object.entries(parsedAttrs)
          .map(([key, value]) => {
            const formattedKey = this.util.capitalizeFirstLetter(key.split('_').join(' '));
            return `• ${formattedKey}: ${value}`;
          })
          .join('\n');
      }

      // Fallback for other cases
      return `• ${parsedAttrs}`;
    } catch (e) {
      console.error('Error formatting custom attributes:', e);
      return 'None';
    }
  }

  // Helper method to format attribute values
  private formatAttributeValue(value: any): string {
    if (value === null || value === undefined) return 'None';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return value.toString();
  }

  // Helper method to format custom attributes in creation style for UserCategory updates
  private formatCustomAttributesForUpdate(attrs: any): string {
    if (!attrs || (Array.isArray(attrs) && attrs.length === 0)) {
      return 'None';
    }

    try {
      // Parse JSON string if needed
      const parsedAttrs = typeof attrs === 'string' ? JSON.parse(attrs) : attrs;

      // Handle array of attribute objects
      if (Array.isArray(parsedAttrs)) {
        return parsedAttrs.map(attr => {
          // Handle simple string case
          if (typeof attr === 'string') {
            return `• ${attr}`;
          }

          // Handle attribute object case in creation style format
          const label = attr.label || attr.labelName || 'Unknown';
          const isMandatory = attr.isMandatory ? 'Yes' : 'No';
          const isDeleted = attr.is_deleted ? 'Yes' : 'No';
          const attributeType = attr.attributeType || 'Text';
          const isValidationNeeded = attr.isValidationNeeded ? 'Yes' : 'No';

          let formatted = `• ${label}<br>`;
          formatted += `&nbsp;&nbsp;- Label Name: ${label}<br>`;
          formatted += `&nbsp;&nbsp;- Is Deleted: ${isDeleted}<br>`;
          formatted += `&nbsp;&nbsp;- Is Mandatory: ${isMandatory}<br>`;
          formatted += `&nbsp;&nbsp;- Attribute Type: ${attributeType}`;

          if (attr.isValidationNeeded !== null) {
            formatted += `<br>&nbsp;&nbsp;- Is Validation Needed: ${isValidationNeeded}`;
          }

          return formatted;
        }).join('\n\n'); // Add extra line between different attributes
      }

      // Handle single object case
      if (typeof parsedAttrs === 'object') {
        const label = parsedAttrs.label || parsedAttrs.labelName || 'Unknown';
        const isMandatory = parsedAttrs.isMandatory ? 'Yes' : 'No';
        const isDeleted = parsedAttrs.is_deleted ? 'Yes' : 'No';
        const attributeType = parsedAttrs.attributeType || 'Text';
        const isValidationNeeded = parsedAttrs.isValidationNeeded ? 'Yes' : 'No';

        let formatted = `• ${label}<br>`;
        formatted += `&nbsp;&nbsp;- Label Name: ${label}<br>`;
        formatted += `&nbsp;&nbsp;- Is Deleted: ${isDeleted}<br>`;
        formatted += `&nbsp;&nbsp;- Is Mandatory: ${isMandatory}<br>`;
        formatted += `&nbsp;&nbsp;- Attribute Type: ${attributeType}`;

        if (parsedAttrs.isValidationNeeded !== null) {
          formatted += `<br>&nbsp;&nbsp;- Is Validation Needed: ${isValidationNeeded}`;
        }

        return formatted;
      }

      // Fallback for other cases
      return `• ${parsedAttrs}`;
    } catch (e) {
      console.error('Error formatting custom attributes for update:', e);
      return 'None';
    }
  }

  // Helper method to find changed custom attribute by comparing oldv and newv arrays
  private findChangedCustomAttributeByComparison(oldAttrs: any[], newAttrs: any[]): { old: any, new: any } | null {
    if (!oldAttrs || !newAttrs || !Array.isArray(oldAttrs) || !Array.isArray(newAttrs)) {
      return null;
    }

    // Find the last attribute in newAttrs that has newCustomAttribute: false (the one being edited)
    const lastNewAttr = newAttrs[newAttrs.length - 1];
    if (!lastNewAttr || lastNewAttr.newCustomAttribute !== false) {
      return null;
    }

    // Compare each attribute at the same index position in both arrays to find the old value
    for (let i = 0; i < Math.min(oldAttrs.length, newAttrs.length); i++) {
      const oldAttr = oldAttrs[i];
      const newAttr = newAttrs[i];

      // If any tracked property differs at the same index, use this old attribute as old value
      if (this.areCustomAttributesDifferent(oldAttr, newAttr)) {
        return {
          old: oldAttr,
          new: lastNewAttr // Always use the last attribute from newv as new value
        };
      }
    }

    // If no matching attribute found in oldAttrs, it's a new attribute
    return {
      old: null,
      new: lastNewAttr
    };
  }

  // Helper method to compare two custom attributes
  private areCustomAttributesDifferent(oldAttr: any, newAttr: any): boolean {
    const keyProperties = ['label', 'labelName', 'attributeType', 'isMandatory', 'isValidationNeeded'];

    for (const prop of keyProperties) {
      if (oldAttr[prop] !== newAttr[prop]) {
        return true;
      }
    }

    return false;
  }


  areValuesEffectivelySame(oldVal: any, newVal: any): boolean {
    // Treat null and undefined as the same empty value
    if (oldVal == null && newVal == null) return true;

    if (Array.isArray(oldVal) && Array.isArray(newVal)) {
      return JSON.stringify(oldVal) === JSON.stringify(newVal);
    }

    // Handle cases where both values are empty/whitespace
    if (typeof oldVal === 'string' && typeof newVal === 'string') {
      return oldVal.trim() === newVal.trim();
    }

    // Handle null/undefined cases
    if (oldVal == null && newVal == null) return true;

    // Handle empty arrays/objects
    if (Array.isArray(oldVal) && Array.isArray(newVal)) {
      return JSON.stringify(oldVal) === JSON.stringify(newVal);
    }

    if (typeof oldVal === 'object' && typeof newVal === 'object') {
      return JSON.stringify(oldVal) === JSON.stringify(newVal);
    }

    return oldVal === newVal;
  }

  formatPermissionChanges(oldPerms: any[], newPerms: any[]): any[] {
    const changes = [];

    // Find added permissions
    const added = this.findPermissionDifferences(newPerms, oldPerms);
    if (added.length) {
      changes.push({
        label: 'Added Permissions',
        oldValue: '',
        newValue: this.formatPermissionList(added)
      });
    }

    // Find removed permissions
    const removed = this.findPermissionDifferences(oldPerms, newPerms);
    if (removed.length) {
      changes.push({
        label: 'Removed Permissions',
        oldValue: this.formatPermissionList(removed),
        newValue: ''
      });
    }

    return changes;
  }

  findPermissionDifferences(source: any[], target: any[]): any[] {
    if (!source) return [];
    if (!target) return source;

    return source.filter(sPerm =>
      !target.some(tPerm => this.permissionsEqual(sPerm, tPerm)))
  }

  permissionsEqual(perm1: any, perm2: any): boolean {
    // Implement logic to compare two permission objects
    return JSON.stringify(perm1) === JSON.stringify(perm2);
  }



  formatPermissionList(perms: any[], isCreation = false): string {
    if (isCreation) {
      return 'Full Access'; // Simplified display for creation
    }

    return perms.map(perm => {
      let permStr = `${perm.name || perm.text} (${perm.access || 'full'})`;

      if (perm.pages && perm.pages.length) {
        permStr += `\n  - Pages: ${this.formatPermissionList(perm.pages)}`;
      }

      if (perm.features && perm.features.length) {
        permStr += `\n  - Features: ${this.formatPermissionList(perm.features)}`;
      }

      return permStr;
    }).join('\n');
  }

  getRevisionHistory(id: any) {
    id = parseInt(id, 10);
    const postData = {
      impactedRowId: id,
    };
    return this.getRevisionHistoryData(postData).pipe(
      switchMap((data: any) => {
        // For questions, fetch versions to map version_id to version_name
        if (this.parentUrl === 'questions' && data.body && data.body.responseBody && data.body.responseBody.length > 0) {
          const firstEntry = data.body.responseBody[0];
          const courseId = firstEntry?.newv?.course_id || firstEntry?.oldv?.course_id;
          const orgId = firstEntry?.newv?.organization_id || firstEntry?.oldv?.organization_id;
          
          if (courseId && orgId) {
            // Fetch versions and tags 
            return forkJoin({
              versions: this.api.post('/getAllCourseVersions', { requestBody: { courseId, courseVersionId: 0, orgId, versionId: 0 } }).pipe(catchError(() => of(null))),
              tags: this.api.get('/tags-master').pipe(catchError(() => of(null)))
            }).pipe(
              map(({ versions, tags }) => {
                // Build version map
                if (versions?.body?.responseBody && Array.isArray(versions.body.responseBody)) {
                  versions.body.responseBody.forEach((version: any) => {
                    this.versionMap.set(version.course_version_id, version.course_version_name);
                  });
                }
                // Build tag map: id -> "key (value)"
                const tagsArray = tags?.body?.responseBody ?? (Array.isArray(tags?.body) ? tags.body : []);
                if (Array.isArray(tagsArray)) {
                  tagsArray.forEach((tag: any) => {
                    if (tag?.id != null) {
                      this.tagMap.set(Number(tag.id), `${tag.key} (${tag.value})`);
                    }
                  });
                }
                return data;
              }),
              catchError(() => of(data)) // If version fetch fails, continue with original data
            );
          }
        }
        return of(data);
      }),
      catchError((error) => {
        const message = `Retrieval error for Revision History: ${error}`;
        return of({ error: message });
      }),
      map((data: any) => {
        const response = data?.body?.responseBody ?? [];
        const replaced = response.map((entry: any) => this.replaceIdsWithNames(entry));

        // --- UserCategory: For the first Created event, only show custom attributes with newCustomAttribute: true ---
        let processedResponse = replaced;
        if (this.parentUrl === 'UserCategory' && replaced.length > 0) {
          const first = replaced[0];
          if (
            (!first.oldv || Object.keys(first.oldv).length === 0) &&
            first.newv &&
            Array.isArray(first.newv.custom_attrs) &&
            first.newv.custom_attrs.some(attr => attr.newCustomAttribute === true)
          ) {
            first.newv.custom_attrs = first.newv.custom_attrs.filter(attr => attr.newCustomAttribute === true);
          }
        }

        this.events = [];

        if (processedResponse.length > 0) {
          // Filter out duplicate MOU entries
          const uniqueResponses = this.filterDuplicateMouEntries(processedResponse);

          // Consolidate creation events with their permission updates
          const consolidatedResponses = this.consolidateCreationEvents(uniqueResponses);

          // For EvaluationCriteria: merge all companion records into the main EC record.
          // The backend produces multiple audit rows per save (one per table touched).
          // The main record has 'name'; companion records carry tags, evaluation_criteria, or
          // grading_criteria. Merge them all into the record that has 'name' and discard the rest.
          if (this.parentUrl === 'EvaluationCriteria') {
            // Find all main EC records (those that have a name in newv)
            const mainEntries = consolidatedResponses.filter((e: any) => e?.newv?.name);
            const subIndices = new Set<number>();

            consolidatedResponses.forEach((entry: any, idx: number) => {
              // Skip main entries themselves
              if (entry?.newv?.name) return;

              // This is a companion record — find the closest main entry by modified_date proximity
              const entryTime = entry?.modified_date ? new Date(entry.modified_date).getTime() : 0;
              let bestMain: any = null;
              let bestDiff = Infinity;
              mainEntries.forEach((main: any) => {
                const mainTime = main?.modified_date ? new Date(main.modified_date).getTime() : 0;
                const diff = Math.abs(entryTime - mainTime);
                if (diff < bestDiff) { bestDiff = diff; bestMain = main; }
              });

              if (!bestMain && mainEntries.length > 0) {
                bestMain = mainEntries[0];
              }

              if (bestMain) {
                bestMain.newv = bestMain.newv || {};
                if (entry?.newv?.tags) {
                  bestMain.newv.tags = entry.newv.tags;
                }
                if (entry?.newv?.evaluation_criteria && !bestMain.newv.evaluation_criteria) {
                  bestMain.newv.evaluation_criteria = entry.newv.evaluation_criteria;
                }
                if (entry?.newv?.grading_criteria && !bestMain.newv.grading_criteria) {
                  bestMain.newv.grading_criteria = entry.newv.grading_criteria;
                }
                subIndices.add(idx);
              }
            });

            // Remove companion records that were merged
            const filteredForEC = consolidatedResponses.filter((_: any, i: number) => !subIndices.has(i));
            consolidatedResponses.length = 0;
            filteredForEC.forEach((e: any) => consolidatedResponses.push(e));
          }

          // Track latest group_candidates across all events (for assessmentPlan)
          let latestGroupCandidatesMeta: any = null;

          let lastDraftBatchTime: number | null = null;
          let lastPublishBatchTime: number | null = null;


          let qpDisplayName: string | null = null;
          consolidatedResponses.forEach((e: any) => {
            const n = e?.newv?.name ?? e?.oldv?.name;
            if (n && typeof n === 'string') qpDisplayName = n;
          });


          const publishTimes: number[] = consolidatedResponses
            .filter((e: any) => e?.audit_table_name === 'published_question_paper')
            .map((e: any) => e?.modified_date ? new Date(e.modified_date).getTime() : null)
            .filter((t: number | null): t is number => t != null);
          const isNearPublish = (t: number | null): boolean =>
            t != null && publishTimes.some(pt => Math.abs(t - pt) <= 5000);

          consolidatedResponses.forEach((element: any, index: number) => {
            // Update latest group_candidates tracker
            const gcRaw = element?.newv?.group_candidates || element?.oldv?.group_candidates;
            if (gcRaw) {
              try {
                latestGroupCandidatesMeta = typeof gcRaw === 'string' ? JSON.parse(gcRaw) : gcRaw;
              } catch (e) { /* keep previous */ }
            }

            if (element?.audit_table_name === 'question_paper_mapping_with_questions') {
              const thisTime = element?.modified_date ? new Date(element.modified_date).getTime() : null;


              if (isNearPublish(thisTime)) return;

              const isSameBatch = lastDraftBatchTime != null && thisTime != null && Math.abs(thisTime - lastDraftBatchTime) <= 5000;
              lastDraftBatchTime = thisTime;
              if (isSameBatch) return; 

              const nameLine = qpDisplayName ? `<br>Question Paper: ${qpDisplayName}` : '';
              const timeLine = `<br>Time: ${this.formatDateTime(element.modified_date)}`;
              this.events.push({
                sno: index + 1,
                title: 'Updated',
                content: [{
                  label: 'Question Paper saved as Draft',
                  oldValue: null,
                  newValue: `Question Paper saved as Draft${nameLine}${timeLine}`,
                  isHtml: true
                }],
                date: element.modified_date,
                user: element?.modified_user || element.created_user,
              });
              return;
            }
            if (element?.audit_table_name === 'published_question_paper') {
              const thisTime = element?.modified_date ? new Date(element.modified_date).getTime() : null;
              const isSameBatch = lastPublishBatchTime != null && thisTime != null && Math.abs(thisTime - lastPublishBatchTime) <= 5000;
              lastPublishBatchTime = thisTime;
              if (isSameBatch) return;

              const nameLine = qpDisplayName ? `<br>Question Paper: ${qpDisplayName}` : '';
              const timeLine = `<br>Time: ${this.formatDateTime(element.modified_date)}`;
              this.events.push({
                sno: index + 1,
                title: 'Updated',
                content: [{
                  label: 'Question Paper published',
                  oldValue: null,
                  newValue: `Question Paper published${nameLine}${timeLine}`,
                  isHtml: true
                }],
                date: element.modified_date,
                user: element?.modified_user || element.created_user,
              });
              return;
            }

            element = this.removeNullEntries(element);

            // Check if oldv is null, undefined, or completely empty
            const isCreationEvent = !element?.oldv || Object.keys(element.oldv).length === 0;

            // Check if oldv has real values (not empty, not null, not all 'None'/'')
            const hasRealOldValues = !isCreationEvent && Object.keys(element.oldv).some(key => {
              const oldValue = element.oldv[key];
              return oldValue !== null && oldValue !== undefined && oldValue !== 'None' && oldValue !== '';
            });

            // Check if there are actual changes between oldv and newv
            const changedKeysForEvent = element?.oldv && element?.newv ? this.findChangedKeys(element.oldv, element.newv) : [];
            const hasChanges = changedKeysForEvent.length > 0;

            // Evaluation criteria sub-records embedded in QPV revision history:
            // treat as creation only when oldv has no 'name' field (genuine first insert).
            // Not needed when parentUrl is EvaluationCriteria — those are already merged above.
            const isECSubRecord = this.parentUrl !== 'EvaluationCriteria'
              && element?.audit_table_name === 'question_paper_evaluation_criteria'
              && !element?.oldv?.name;

            // Determine if this is a true update (oldv has real values AND has changes)
            const isTrueUpdate = !isECSubRecord && hasRealOldValues && hasChanges;

            // Handle UPDATE events (only when oldv has real previous values and they differ from newv)
            if (isTrueUpdate && element?.newv) {
              const previousEntry = index > 0 ? consolidatedResponses[index - 1] : null;
              const previousModifiedDate = previousEntry?.modified_date || element?.created_date;
              const content = this.getContentMapping(element, previousModifiedDate);
              if (content) {
                // For assessmentPlan: inject candidate summary if not already in this diff
                if (this.parentUrl === 'assessmentPlan' && latestGroupCandidatesMeta) {
                  const hasCandidatesEntry = content.some((c: any) => c.label === 'Candidates');
                  if (!hasCandidatesEntry) {
                    const meta = latestGroupCandidatesMeta;
                    const lines = [];
                    if (meta.total !== undefined) lines.push(`Total no of candidates: ${meta.total}`);
                    if (meta.published !== undefined) lines.push(`Published candidates: ${meta.published}`);
                    if (meta.added !== undefined) lines.push(`Added candidates: ${meta.added}`);
                    if (meta.removed !== undefined) lines.push(`Removed candidates: ${meta.removed}`);
                    content.push({
                      label: 'Candidates',
                      oldValue: null,
                      newValue: lines.join('<br>')
                    });
                    if (meta.file_url) {
                      content.push({
                        label: 'File Url',
                        oldValue: null,
                        newValue: meta.file_url,
                        isFile: true
                      });
                    }
                  }
                }
                this.events.push({
                  sno: index + 1,
                  title: 'Updated',
                  content,
                  date: element.modified_date,
                  user: element.modified_user,
                });
              }
            } else {
              // Handle CREATION events (when oldv is empty/null or has no real values)
              if (element.created_user_username || element.newv) {
                element.newv.created_user_username = element.created_user_username;
              }

              // Evaluation criteria sub-record embedded inside QPV revision history
              if (isECSubRecord) {
                const creationData = element.newv || {};
                const creationContent: any[] = [];

                if (creationData.name) {
                  creationContent.push({ label: 'Title', newValue: creationData.name });
                }
                if (creationData.description) {
                  creationContent.push({ label: 'Description', newValue: creationData.description });
                }
                if (creationData.tags) {
                  try {
                    const tags = typeof creationData.tags === 'string' ? JSON.parse(creationData.tags) : creationData.tags;
                    if (Array.isArray(tags) && tags.length) {
                      tags.forEach((t: any) => {
                        if (!t || !t.key) return;
                        const val = t[t.key] != null ? t[t.key] : '';
                        creationContent.push({ label: 'Tag Type', newValue: t.key });
                        creationContent.push({ label: 'Tag Values', newValue: val });
                      });
                    }
                  } catch (e) {}
                }
                if (creationData.evaluation_criteria) {
                  try {
                    const ec = typeof creationData.evaluation_criteria === 'string'
                      ? JSON.parse(creationData.evaluation_criteria) : creationData.evaluation_criteria;
                    Object.keys(ec).forEach(param => {
                      const rows = ec[param];
                      if (Array.isArray(rows)) {
                        rows.forEach(r => {
                          creationContent.push({
                            label: 'Evaluation Parameter',
                            newValue: `Evaluation Parameter: ${param}<br>Type: ${r.item || '—'}<br>Minimum Pass %: ${r.value ?? '—'}`,
                            isHtml: true
                          });
                        });
                      }
                    });
                  } catch (e) {}
                }
                if (creationData.grading_criteria) {
                  try {
                    const gc = typeof creationData.grading_criteria === 'string'
                      ? JSON.parse(creationData.grading_criteria) : creationData.grading_criteria;
                    Object.keys(gc).forEach(grade => {
                      if (!grade) return;
                      const g = gc[grade];
                      creationContent.push({
                        label: 'Grading Criteria',
                        newValue: `Grade: ${grade}<br>Minimum Marks in percentage (%): ${g.min ?? '—'}<br>Maximum Marks in percentage (%): ${g.max ?? '—'}`,
                        isHtml: true
                      });
                    });
                  } catch (e) {}
                }
                if (creationContent.length) {
                  this.events.push({
                    sno: index + 1,
                    title: 'Created',
                    content: creationContent,
                    date: element.modified_date,
                    user: element?.modified_user || element.created_user,
                  });
                }
              } else
              // For UserCategory, create a special creation event with custom attributes that were added during creation
              if (this.parentUrl === 'UserCategory' &&
                (!element?.oldv || Object.keys(element.oldv).length === 0)) {

                const creationData = { ...element.newv };
                const newCustomAttrs = (element?.newv?.custom_attrs && Array.isArray(element.newv.custom_attrs))
                  ? element.newv.custom_attrs.filter(attr => attr.newCustomAttribute === true)
                  : [];

                // Build a structured content array for creation event so each custom attribute
                // is shown as a separate entry similar to update events
                const creationContent: any[] = [];

                // Add basic fields like Category Name, Is Active etc.
                const keysToShow = ['category_name', 'is_active', 'organization_name', 'created_user_username'];
                keysToShow.forEach(k => {
                  if (creationData[k] !== undefined && creationData[k] !== null) {
                    creationContent.push({
                      label: this.getLabelName(k, element),
                      oldValue: 'None',
                      newValue: creationData[k]
                    });
                  }
                });

                // Add created_datetime explicitly (if available)
                let displayDate: any = undefined;
                if (creationData.created_datetime) {
                  // Determine how to format created_datetime for display.
                  // If it's an ISO timestamp (YYYY-MM-DDT...), treat it as UTC and convert to local time.
                  let displayDate = creationData.created_datetime;
                  try {
                    const isIsoLike = typeof creationData.created_datetime === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(creationData.created_datetime);
                    const ddmmRegex = /^\d{2}\/\d{2}\/\d{4}, \d{2}:\d{2}:\d{2}$/;
                    if (typeof creationData.created_datetime === 'string' && ddmmRegex.test(creationData.created_datetime)) {
                      // Already in desired display format
                      displayDate = creationData.created_datetime;
                    } else if (isIsoLike) {
                      const m = moment.utc(creationData.created_datetime);
                      if (m.isValid()) {
                        displayDate = m.local().format('DD/MM/YYYY, HH:mm:ss');
                      }
                    } else {
                      const m = moment(creationData.created_datetime);
                      if (m.isValid()) {
                        displayDate = m.format('DD/MM/YYYY, HH:mm:ss');
                      }
                    }
                  } catch (e) {
                    // fallback to raw value
                  }
                  creationContent.push({
                    label: this.getLabelName('created_datetime', element),
                    oldValue: 'None',
                    newValue: displayDate
                  });
                }

                // Add each custom attribute as a separate content entry
                if (newCustomAttrs.length > 0) {
                  newCustomAttrs.forEach((attr: any) => {
                    creationContent.push({
                      label: 'Custom Attribute',
                      oldValue: 'None',
                      newValue: this.formatCustomAttributesForUpdate([attr])
                    });
                  });
                }

                this.events.push({
                  sno: index + 1,
                  title: 'Created',
                  content: creationContent,
                  date: (typeof displayDate !== 'undefined' ? displayDate : creationData.created_datetime) || element.modified_date,
                  user: element?.modified_user,
                });
              } else if (this.parentUrl === 'assessmentPlan' &&
                (!element?.oldv || Object.keys(element.oldv).length === 0)) {
                // Structured creation event for assessmentPlan
                const creationData = element.newv || {};
                const creationContent: any[] = [];

                const extractProjectFromTags = (tags: any): string => {
                  if (!tags) return null;
                  try {
                    const parsed = typeof tags === 'string' ? JSON.parse(tags) : tags;
                    if (Array.isArray(parsed)) {
                      const projectTag = parsed.find((t: any) => t.type === 'Project' || t.key === 'Project');
                      if (projectTag) return projectTag['Project'] || projectTag.value || null;
                    }
                  } catch (e) {}
                  return null;
                };

                const keysToShow = ['title', 'description', 'is_active'];
                keysToShow.forEach(k => {
                  if (creationData[k] !== undefined && creationData[k] !== null && creationData[k] !== '') {
                    const label = k === 'is_active' ? 'Status' : this.util.capitalizeFirstLetter(k.split('_').join(' '));
                    creationContent.push({
                      label,
                      oldValue: 'None',
                      newValue: k === 'is_active' ? (creationData[k] ? 'Active' : 'Inactive') : creationData[k]
                    });
                  }
                });

                const projectName = extractProjectFromTags(creationData.tags);
                if (projectName) {
                  creationContent.push({
                    label: 'Project',
                    oldValue: 'None',
                    newValue: projectName
                  });
                }

                this.events.push({
                  sno: index + 1,
                  title: 'Created',
                  content: creationContent.length ? creationContent : JSON.stringify(creationData, null, 2),
                  date: element.modified_date,
                  user: element?.modified_user || element.created_user,
                });
              } else if (this.parentUrl === 'EvaluationCriteria' &&
                (!element?.oldv || Object.keys(element.oldv).length === 0)) {
                // Structured creation event for EvaluationCriteria
                const creationData = element.newv || {};
                const creationContent: any[] = [];

                // Title (Alias)
                if (creationData.name) {
                  creationContent.push({ label: 'Title', newValue: creationData.name });
                }

                // Description
                if (creationData.description) {
                  creationContent.push({ label: 'Description', newValue: creationData.description });
                }

                // Tags — show each tag type and its value as separate rows
                if (creationData.tags) {
                  try {
                    const tags = typeof creationData.tags === 'string' ? JSON.parse(creationData.tags) : creationData.tags;
                    if (Array.isArray(tags) && tags.length) {
                      tags.forEach((t: any) => {
                        if (!t || !t.key) return;
                        const val = t[t.key] != null ? t[t.key] : '';
                        creationContent.push({ label: 'Tag Type', newValue: t.key });
                        creationContent.push({ label: 'Tag Values', newValue: val });
                      });
                    }
                  } catch (e) {}
                }

                // Evaluation parameters — each row as a separate entry
                if (creationData.evaluation_criteria) {
                  try {
                    const ec = typeof creationData.evaluation_criteria === 'string'
                      ? JSON.parse(creationData.evaluation_criteria) : creationData.evaluation_criteria;
                    Object.keys(ec).forEach(param => {
                      const rows = ec[param];
                      if (Array.isArray(rows)) {
                        rows.forEach(r => {
                          const lines = `Evaluation Parameter: ${param}<br>Type: ${r.item || '—'}<br>Minimum Pass %: ${r.value ?? '—'}`;
                          creationContent.push({ label: 'Evaluation Parameter', newValue: lines, isHtml: true });
                        });
                      }
                    });
                  } catch (e) {}
                }

                // Grading criteria — each grade as a separate entry
                if (creationData.grading_criteria) {
                  try {
                    const gc = typeof creationData.grading_criteria === 'string'
                      ? JSON.parse(creationData.grading_criteria) : creationData.grading_criteria;
                    Object.keys(gc).forEach(grade => {
                      if (!grade) return;
                      const g = gc[grade];
                      const lines = `Grade: ${grade}<br>Minimum Marks in percentage (%): ${g.min ?? '—'}<br>Maximum Marks in percentage (%): ${g.max ?? '—'}`;
                      creationContent.push({ label: 'Grading Criteria', newValue: lines, isHtml: true });
                    });
                  } catch (e) {}
                }

                this.events.push({
                  sno: index + 1,
                  title: 'Created',
                  content: creationContent.length ? creationContent : JSON.stringify(creationData, null, 2),
                  date: element.modified_date,
                  user: element?.modified_user || element.created_user,
                });
              } else if (this.parentUrl === 'label' &&
                (!element?.oldv || Object.keys(element.oldv).length === 0)) {
                // Special handling for label creation - show label with its children hierarchy
                const labelCreationContent = this.buildLabelCreationContent(element, processedResponse);
                this.events.push({
                  sno: index + 1,
                  title: 'Created',
                  content: labelCreationContent,
                  date: element.modified_date,
                  user: element?.modified_user || element.created_user,
                });
              } else if (this.parentUrl === 'questionPaperVersions' &&
                (!element?.oldv || Object.keys(element.oldv).length === 0)) {
                // Structured creation event for QPV
                const creationData = element.newv || {};
                const creationContent: any[] = [];

                // Fields handled separately or internal — skip from general loop
                const QPV_SPECIAL_FIELDS = new Set([
                  'category', 'question_grouping_logic', 'grouping_criteria', 'labels', 'label',
                  'id', 'organization_id', 'created_user_id', 'modified_user_id', 'question_paper_id',
                  'modified_datetime',
                ]);

                // Show all newv fields (except special ones) with proper label names
                Object.keys(creationData).forEach(k => {
                  if (QPV_SPECIAL_FIELDS.has(k)) return;
                  const val = creationData[k];
                  if (val === undefined || val === null || val === '') return;
                  let displayVal: any = val;
                  if (typeof val === 'boolean') {
                    displayVal = val ? 'Yes' : 'No';
                  } else if (typeof val === 'object') {
                    return; // skip embedded objects — handled separately
                  }
                  const label = this.getLabelName(k, element);
                  creationContent.push({ label, oldValue: 'None', newValue: displayVal });
                });

                if (creationData.label) {
                  try {
                    const groups = typeof creationData.label === 'string' ? JSON.parse(creationData.label) : creationData.label;
                    const buildTreeById = (labels: any[], parentUniqueId: any): string => {
                      const children = labels.filter((l: any) => l.__parentUniqueId === parentUniqueId);
                      if (!children.length) return '';
                      return children.map((child: any) => {
                        const name = child.labelName || child.label_name || '';
                        const grandchildren = buildTreeById(labels, child.__uniqueId);
                        if (grandchildren) {
                          return `<details style="margin-left:20px"><summary style="cursor:pointer;padding:2px 0">&#9675; ${name}</summary><div>${grandchildren}</div></details>`;
                        }
                        return `<div style="margin-left:20px;padding:2px 0">&#9675; ${name}</div>`;
                      }).join('');
                    };

                    if (Array.isArray(groups) && groups.length) {

                      const byUniqueId = new Map<any, any>();
                      groups.forEach((group: any) => {
                        const groupLabels = Array.isArray(group.labels) ? group.labels : [];
                        const labelIdToUniqueId = new Map<any, any>();
                        groupLabels.forEach((l: any) => {
                          const lid = l.labelId !== undefined ? l.labelId : l.label_id;
                          const uid = l.id;
                          if (uid != null && lid != null) labelIdToUniqueId.set(lid, uid);
                        });
                        groupLabels.forEach((l: any) => {
                          const uid = l.id;
                          if (uid == null) return;
                          const pLabelId = l.parentLabelId !== undefined ? l.parentLabelId : l.parent_label_id;
                          const parentUniqueId = (pLabelId !== null && pLabelId !== undefined)
                            ? (labelIdToUniqueId.get(pLabelId) ?? null)
                            : null;
                          const existing = byUniqueId.get(uid);
                          if (!existing || parentUniqueId != null) {
                            byUniqueId.set(uid, { ...l, __uniqueId: uid, __parentUniqueId: parentUniqueId });
                          }
                        });
                      });

                      const allLabels = Array.from(byUniqueId.values());
                      const roots = allLabels.filter((l: any) => l.__parentUniqueId == null);

                      const groupLines: string[] = [];
                      roots.forEach((rootLabel: any) => {
                        const name = rootLabel.labelName || rootLabel.label_name || '';
                        const subtree = buildTreeById(allLabels, rootLabel.__uniqueId);
                        if (subtree) {
                          groupLines.push(`<details style="margin-bottom:4px"><summary style="cursor:pointer;padding:2px 0;font-weight:500">&#9675; ${name}</summary><div>${subtree}</div></details>`);
                        } else {
                          groupLines.push(`<div style="padding:2px 0;font-weight:500">&#9675; ${name}</div>`);
                        }
                      });
                      if (groupLines.length) {
                        creationContent.push({ label: 'QP Label', oldValue: 'None', newValue: groupLines.join(''), isHtml: true });
                      }
                    }
                  } catch (e) {}
                }

                // category → Assign Marks
                if (creationData.category) {
                  try {
                    const cat = typeof creationData.category === 'string' ? JSON.parse(creationData.category) : creationData.category;
                    const parseArr = (v: any): any[] => {
                      if (!v) return [];
                      try { return typeof v === 'string' ? JSON.parse(v) : (Array.isArray(v) ? v : []); } catch (e) { return []; }
                    };
                    const labels = parseArr(creationData.labels ?? creationData.label);

                    const buildLabelTree = (labelIds: number[], lbls: any[]): string => {
                      const allLabels: any[] = [];
                      lbls.forEach((g: any) => { if (Array.isArray(g.labels)) g.labels.forEach((l: any) => allLabels.push(l)); });
                      const selected = allLabels.filter((l: any) => labelIds.includes(l.labelId ?? l.id));
                      if (!selected.length) return 'No labels';
                      const buildTree = (parentId: any): string =>
                        selected.filter((l: any) => (l.parentLabelId ?? l.parent_label_id) === parentId)
                          .map((child: any) => {
                            const name = child.labelName || child.label_name || '';
                            const cid = child.labelId ?? child.id;
                            const sub = buildTree(cid);
                            return sub
                              ? `<details style="margin-left:20px"><summary style="cursor:pointer;padding:2px 0">&#9675; ${name}</summary><div>${sub}</div></details>`
                              : `<div style="margin-left:20px;padding:2px 0">&#9675; ${name}</div>`;
                          }).join('');
                      const roots = selected.filter((l: any) => { const p = l.parentLabelId ?? l.parent_label_id; return p === null || p === undefined || p === 0; });
                      if (!roots.length) return selected.map((l: any) => `<div style="padding:2px 0">&#9675; ${l.labelName || l.label_name || ''}</div>`).join('');
                      return roots.map((root: any) => {
                        const name = root.labelName || root.label_name || '';
                        const rid = root.labelId ?? root.id;
                        const sub = buildTree(rid);
                        return sub
                          ? `<details style="margin-bottom:4px"><summary style="cursor:pointer;padding:2px 0;font-weight:500">&#9675; ${name}</summary><div>${sub}</div></details>`
                          : `<div style="padding:2px 0;font-weight:500">&#9675; ${name}</div>`;
                      }).join('');
                    };

                    const parts: string[] = [];
                    if (cat && typeof cat === 'object') {
                      Object.keys(cat).forEach(evalMode => {
                        const levelMap = cat[evalMode];
                        if (!levelMap || typeof levelMap !== 'object') return;
                        Object.keys(levelMap).forEach(diffLevel => {
                          const entries = levelMap[diffLevel];
                          if (!Array.isArray(entries)) return;
                          entries.forEach((entry: any) => {
                            const labelIds: number[] = Array.isArray(entry.labelId) ? entry.labelId : [];
                            let labelTree = 'No labels';
                            if (labels.length) {
                              const built = buildLabelTree(labelIds, labels);
                              if (built && built !== 'No labels') labelTree = built;
                            }
                            if (labelTree === 'No labels' && Array.isArray(entry.labelName) && entry.labelName.filter(Boolean).length) {
                              const names = entry.labelName.filter(Boolean) as string[];
                              if (names.length === 1) {
                                labelTree = `<div style="padding:2px 0;font-weight:500">&#9675; ${names[0]}</div>`;
                              } else {
                                const items = names.map((n: string) => `<div style="padding:2px 0">&#9675; ${n}</div>`).join('');
                                labelTree = `<details style="margin-bottom:4px"><summary style="cursor:pointer;padding:2px 0;font-weight:500">Labels (${names.length})</summary><div style="margin-left:16px">${items}</div></details>`;
                              }
                            }
                            parts.push(
                              `Evaluation Mode: ${evalMode}<br>` +
                              `Difficulty Level: ${diffLevel}<br>` +
                              `Marks: ${entry.nos ?? entry.marks ?? '—'}<br>` +
                              `Label:<br><div style="margin-left:16px">${labelTree}</div>`
                            );
                          });
                        });
                      });
                    }
                    if (parts.length) {
                      creationContent.push({ label: 'Assign Marks', oldValue: 'None', newValue: parts.join('<br><br>'), isHtml: true });
                    }
                  } catch (e) {}
                }

                // grouping_criteria / question_grouping_logic → Grouping Criteria
                const gcRaw = creationData.grouping_criteria ?? creationData.question_grouping_logic;
                if (gcRaw) {
                  try {
                    const gc = typeof gcRaw === 'string' ? JSON.parse(gcRaw) : gcRaw;
                    if (gc && typeof gc === 'object') {
                      const lines = Object.keys(gc).map(evalMode => `Evaluation Mode: ${evalMode}<br>Grouping Criteria: ${gc[evalMode] || '—'}`);
                      if (lines.length) {
                        creationContent.push({ label: 'Assign Grouping Criteria', oldValue: 'None', newValue: lines.join('<br><br>'), isHtml: true });
                      }
                    }
                  } catch (e) {}
                }

                this.events.push({
                  sno: index + 1,
                  title: 'Created',
                  content: creationContent.length ? creationContent : JSON.stringify(creationData, null, 2),
                  date: element.modified_date,
                  user: element?.modified_user || element.created_user,
                });
              } else if (this.parentUrl === 'Invoices' &&
                (!element?.oldv || Object.keys(element.oldv).length === 0)) {
                // Structured creation event for Invoices
                const creationData = element.newv || {};
                const creationContent: any[] = [];
                const invoiceDisplayKeys = [
                  'reference_no', 'party_name', 'invoice_date', 'invoice_value',
                  'total_amount', 'status', 'units', 'tds_deducted', 'service',
                  'type', 'gstin', 'remarks', 'po_number'
                ];
                invoiceDisplayKeys.forEach(k => {
                  if (creationData[k] !== undefined && creationData[k] !== null && creationData[k] !== '') {
                    creationContent.push({
                      label: this.getLabelName(k, element),
                      oldValue: 'None',
                      newValue: String(creationData[k])
                    });
                  }
                });
                this.events.push({
                  sno: index + 1,
                  title: 'Created',
                  content: creationContent.length ? creationContent : JSON.stringify(creationData, null, 2),
                  date: element.modified_date,
                  user: element?.modified_user || element.created_user,
                });
              } else {
                // Regular creation event
                this.events.push({
                  sno: index + 1,
                  title: 'Created',
                  'content': JSON.stringify(element.newv, null, 2),
                  date: element.modified_date,
                  user: element?.modified_user||element.created_user,
                });
              }
            }
          });
        } else {
          this.events = [{
            sno: 0,
            title: 'Created',
            content: 'Created',
          }]
        }

        if (this.parentUrl === 'questionPaperVersions' && Array.isArray(this.events) && this.events.length) {
          const creationEvent = this.events.find((e: any) => e?.title === 'Created');
          const qpCreatorName = creationEvent?.user || this.events[0]?.user;
          if (qpCreatorName) {
            this.events.forEach((e: any) => { e.user = qpCreatorName; });
          }
        }

        // Format all dates before returning
        // Group by user and full datetime (including time) to keep Created and Updated separate
        this.events = groupBy(this.events, response => {
          const dateStr = response?.date || '';
          const datePart = dateStr.split('T')[0]; // e.g., 2026-02-05
          const timePart = dateStr.split('T')[1]?.substring(0, 5) || '00:00'; // e.g., 14:30
          return `${response?.user || ''}:${datePart} ${timePart}`;
        })
        
        // Format dates in grouped events
        Object.keys(this.events).forEach(key => {
          this.events[key].forEach((event: any) => {
            if (event.date) {
              event.date = this.formatDateToEnglish(event.date);
            }
          });
        });

        return this.events;
      }));
  }

  // Helper method to format date to English format (29th Jan 2026)
  private formatDateToEnglish(dateStr: any): string {
    if (!dateStr) return '';
    
    try {
      const m = moment(dateStr);
      if (m.isValid()) {
        return m.format('Do MMM YYYY');
      }
    } catch (e) {
      // fallback
    }
    
    return dateStr?.toString() || '';
  }
  private replaceIdsWithNames(entry: any): any {
    // Define mapping list
    const mappings = [ ['version_id', 'version_name'] ];

    // Helper function to replace ids in a given object
    const replaceInObject = (obj: any, base: any) => {
      mappings.forEach(([idKey, nameKey]) => {
        if (obj && obj[idKey] && idKey === 'version_id') {
          // For version_id, look it up in the versionMap
          const versionName = this.versionMap.get(obj[idKey]);
          if (versionName) {
            obj[nameKey] = versionName; // Attach the version name as a separate field
          }
        } else if (obj && obj[idKey] && base[nameKey]) {
          obj[nameKey] = base[nameKey]; // Attach the name as a separate field
        }
      });

      // Status logic
      if (obj && obj.status_id !== undefined && obj.status_id !== null) {
        obj.status_id = obj.status_id === 2 ? 'Cold' : 'Warm';
      } else if (obj && obj.status_id && base.status_name) {
        obj.status_id = base.status_name;
      }

      // Type logic
      if (obj && obj.type_id !== undefined && obj.type_id !== null) {
        obj.type_id = obj.type_id === 1 ? 'Open' : 'Closed';
      } else if (obj && obj.type_id && base.type_name) {
        obj.type_id = base.type_name;
      }

      // Created user combined
      if (obj && obj.created_user_id && base.created_user && base.created_user_username) {
        obj.created_user_id = `${base.created_user} (${base.created_user_username})`;
      } else if (obj && obj.created_user_id && base.created_user) {
        obj.created_user_id = base.created_user;
      } else if (obj && obj.created_user_id && base.created_user_username) {
        obj.created_user_id = base.created_user_username;
      }
    };

    // Replace top-level fields
    replaceInObject(entry, entry);

    // Replace in oldv and newv
    ['oldv', 'newv'].forEach(key => {
      if (entry[key]) {
        replaceInObject(entry[key], entry);
        if (this.parentUrl === 'questions' && this.tagMap.size > 0 && entry[key].tags) {
          try {
            const tagsRaw = typeof entry[key].tags === 'string' ? JSON.parse(entry[key].tags) : entry[key].tags;
            if (Array.isArray(tagsRaw)) {
              const resolved = tagsRaw.map((t: any) => {
                if (t && t.id != null && Object.keys(t).length === 1) {
                  // Tag stored as {id: X} — resolve to name
                  const name = this.tagMap.get(Number(t.id));
                  return name ? { key: name, id: t.id } : t;
                }
                return t;
              });
              entry[key].tags = resolved;
            }
          } catch (e) {}
        }
      }
    });

    return entry;
  }

  // New method to filter duplicate MOU entries
  private filterDuplicateMouEntries(entries: any[]): any[] {
    // Only process for MOU history
    if (this.parentUrl !== 'manageMOUHistory') {
      return entries;
    }

    const uniqueEntries = [];
    const seenEntries = new Set();

    entries.forEach(entry => {
      // Create a unique key based on the entry content and timestamp
      const entryKey = JSON.stringify({
        newv: entry.newv,
        modified_date: entry.modified_date
      });

      if (!seenEntries.has(entryKey)) {
        seenEntries.add(entryKey);
        uniqueEntries.push(entry);
      }
    });

    return uniqueEntries;
  }

  // Method to consolidate creation events with their permission updates
  private consolidateCreationEvents(entries: any[]): any[] {
    // Only process for Roles, UserCategory, and label modules
    // Removed 'assessmentPlan' from here - assessments should show Created and Updated separately
    if (this.parentUrl !== 'Roles' && this.parentUrl !== 'UserCategory' && this.parentUrl !== 'label') {
      return entries;
    }

    // Special handling for UserCategory - consolidate newCustomAttribute events
    if (this.parentUrl === 'UserCategory') {
      return this.consolidateUserCategoryEvents(entries);
    }

    // Special handling for labels - consolidate hierarchy creations
    if (this.parentUrl === 'label') {
      return this.consolidateLabelHierarchyEvents(entries);
    }

    const consolidated = [];
    const processedIndices = new Set();
    const rolePermissions = new Map(); // Track permissions for each role

    for (let i = 0; i < entries.length; i++) {
      if (processedIndices.has(i)) continue;

      const currentEntry = entries[i];

      // Check if this is a creation event (oldv is null)
      if (!currentEntry.oldv || Object.keys(currentEntry.oldv).length === 0) {
        // Look for a subsequent update event that adds permissions
        let updateEntry = null;
        let updateIndex = -1;

        for (let j = i + 1; j < entries.length; j++) {
          const nextEntry = entries[j];

          // Check if this is a permission update for the same role (Roles module)
          if (nextEntry.oldv &&
            nextEntry.newv &&
            nextEntry.oldv.perm_levels === null &&
            nextEntry.newv.perm_levels &&
            nextEntry.newv.perm_levels.length > 0 &&
            nextEntry.newv.id === currentEntry.newv.id) {

            updateEntry = nextEntry;
            updateIndex = j;
            break;
          }
        }

        if (updateEntry) {
          // Merge the data into the creation event
          const mergedEntry = { ...currentEntry };
          mergedEntry.newv = { ...currentEntry.newv };

          mergedEntry.newv.perm_levels = updateEntry.newv.perm_levels;
          // Store the permissions for this role for future update events
          rolePermissions.set(currentEntry.newv.id, updateEntry.newv.perm_levels);

          consolidated.push(mergedEntry);
          processedIndices.add(i);
          processedIndices.add(updateIndex);
        } else {
          // No update found, add as is
          consolidated.push(currentEntry);
          processedIndices.add(i);
        }
      } else {
        // This is an update event - check if we need to add permissions to oldv
        const itemId = currentEntry.newv?.id || currentEntry.oldv?.id;

        if (itemId && rolePermissions.has(itemId)) {
          // Add the stored permissions to oldv if they're missing
          const updatedEntry = { ...currentEntry };
          if (!updatedEntry.oldv) {
            updatedEntry.oldv = {};
          }
          if (!updatedEntry.oldv.perm_levels) {
            updatedEntry.oldv.perm_levels = rolePermissions.get(itemId);
          }

          // Update stored permissions if new permissions are provided
          if (updatedEntry.newv && updatedEntry.newv.perm_levels) {
            rolePermissions.set(itemId, updatedEntry.newv.perm_levels);
          }

          consolidated.push(updatedEntry);
        } else {
          // No stored data, add as is
          // But still update stored data if new data is provided
          if (currentEntry.newv && currentEntry.newv.perm_levels) {
            const itemId = currentEntry.newv?.id || currentEntry.oldv?.id;
            if (itemId) {
              rolePermissions.set(itemId, currentEntry.newv.perm_levels);
            }
          }
          consolidated.push(currentEntry);
        }
        processedIndices.add(i);
      }
    }

    return consolidated;
  }

  // Special method to consolidate UserCategory events with newCustomAttribute
  private consolidateUserCategoryEvents(entries: any[]): any[] {
    const consolidated = [];
    const processedIndices = new Set();

    for (let i = 0; i < entries.length; i++) {
      if (processedIndices.has(i)) continue;

      const currentEntry = entries[i];

      // Check if this is a creation event (oldv is null)
      if (!currentEntry.oldv || Object.keys(currentEntry.oldv).length === 0) {
        // Look for subsequent events that add custom attributes with newCustomAttribute: true
        const newCustomAttributeEvents = [];
        let lastProcessedIndex = i;

        // Also include the current event if it has custom attributes with newCustomAttribute: true
        if (currentEntry.newv?.custom_attrs &&
          Array.isArray(currentEntry.newv.custom_attrs) &&
          currentEntry.newv.custom_attrs.some(attr => attr.newCustomAttribute === true)) {
          newCustomAttributeEvents.push(currentEntry);
        }

        for (let j = i + 1; j < entries.length; j++) {
          if (processedIndices.has(j)) continue;

          const nextEntry = entries[j];

          // Check if this is the same user category
          if (nextEntry.newv?.id !== currentEntry.newv?.id) {
            break;
          }

          // Only merge events where oldv is empty AND have newCustomAttribute: true (true creation events)
          if (
            (!nextEntry.oldv || Object.keys(nextEntry.oldv).length === 0) &&
            nextEntry.newv?.custom_attrs &&
            Array.isArray(nextEntry.newv.custom_attrs) &&
            nextEntry.newv.custom_attrs.some(attr => attr.newCustomAttribute === true)
          ) {
            newCustomAttributeEvents.push(nextEntry);
            lastProcessedIndex = j;
          } else {
            // As soon as we hit an event with oldv (an update), stop merging
            break;
          }
        }

        // Merge all newCustomAttribute events into the creation event
        const mergedEntry = { ...currentEntry };
        mergedEntry.newv = { ...currentEntry.newv };

        // Collect all custom attributes that were ever added during creation (newCustomAttribute: true)
        const allCreationAttributes = new Map(); // Use Map to avoid duplicates by label

        // Go through all events to find attributes that were originally added during creation
        for (let k = 0; k < entries.length; k++) {
          const entry = entries[k];
          if (entry.newv?.custom_attrs && Array.isArray(entry.newv.custom_attrs)) {
            entry.newv.custom_attrs.forEach(attr => {
              if (attr.newCustomAttribute === true) {
                // Store by label to avoid duplicates, keep the latest version
                allCreationAttributes.set(attr.label, attr);
              }
            });
          }
        }

        // Set the creation event to include all attributes that were ever added during creation
        if (allCreationAttributes.size > 0) {
          mergedEntry.newv.custom_attrs = Array.from(allCreationAttributes.values());
        }

        // Update the modified date to the last event's date
        if (newCustomAttributeEvents.length > 0) {
          const lastEvent = newCustomAttributeEvents[newCustomAttributeEvents.length - 1];
          mergedEntry.modified_date = lastEvent.modified_date;
          mergedEntry.modified_user = lastEvent.modified_user;
        }

        consolidated.push(mergedEntry);

        // Mark all processed entries as handled
        processedIndices.add(i);
        for (let k = i + 1; k <= lastProcessedIndex; k++) {
          processedIndices.add(k);
        }
      } else {
        // This is a regular update event (not part of initial creation)
        consolidated.push(currentEntry);
        processedIndices.add(i);
      }
    }

    return consolidated;
  }

  private consolidateLabelHierarchyEvents(entries: any[]): any[] {
    // First pass: identify consolidated array entries and their label IDs
    const consolidatedHierarchyLabels = new Set<number>();
    const consolidatedEntries: any[] = [];
    const individualEntries: any[] = [];

    entries.forEach(entry => {
      if (Array.isArray(entry.newv)) {
        // This is a consolidated hierarchy entry - track all involved label IDs
        entry.newv.forEach((label: any) => {
          consolidatedHierarchyLabels.add(label.labelId || label.label_id);
        });
        consolidatedEntries.push(entry);
      } else {
        // Check if this individual entry is part of a consolidated hierarchy
        const labelId = entry.newv?.label_id;
        const isPartOfHierarchy = consolidatedHierarchyLabels.has(labelId);
        
        if (isPartOfHierarchy) {
          // Only skip CREATION entries that are part of hierarchy
          // ALWAYS include UPDATE entries (where oldv has real values)
          const isCreationEvent = !entry.oldv || Object.keys(entry.oldv).length === 0;
          
          if (isCreationEvent) {
            // Skip creation - already shown in hierarchy
            return;
          }
          // Include update entries - they show individual label modifications
        }
        
        individualEntries.push(entry);
      }
    });

    // Return consolidated entries first, then individual entries
    const result = [...consolidatedEntries, ...individualEntries];

    // Fallback to original consolidation logic for non-array cases
    if (result.length === entries.length) {
      return this.consolidateLabelHierarchyEventsOld(entries);
    }

    return result;
  }

  // Original consolidation logic for single-object entries
  private consolidateLabelHierarchyEventsOld(entries: any[]): any[] {
    // Entries come in reverse chronological order from DB (newest first)
    // For proper hierarchy consolidation, we need chronological order (oldest first)
    // So reverse temporarily for processing, then reverse back to maintain display order
    
    const chronologicalEntries = [...entries].reverse();
    const consolidated = [];
    const processedIndices = new Set();

    for (let i = 0; i < chronologicalEntries.length; i++) {
      if (processedIndices.has(i)) continue;

      const currentEntry = chronologicalEntries[i];

      // Check if this is a creation event (oldv is empty or has very few fields)
      if (!currentEntry.oldv || Object.keys(currentEntry.oldv).length === 0) {
        if (currentEntry.newv?.parent_label_id === 0 || currentEntry.newv?.parent_label_id === null) {
          // This is a root label creation - find ALL descendants (children, grandchildren, etc.) created nearby
          const rootLabelId = currentEntry.newv.label_id;
          const creationTime = new Date(currentEntry.modified_date).getTime();
          const courseId = currentEntry.newv.course_id;
          const versionId = currentEntry.newv.version_id;
          
          const descendantIndices = [];
          const processedLabelIds = new Set<number>();
          processedLabelIds.add(rootLabelId);

          // Recursively find all descendants
          const findDescendants = (parentLabelId: number) => {
            for (let j = i + 1; j < chronologicalEntries.length; j++) {
              if (processedIndices.has(j) || descendantIndices.includes(j)) continue;

              const nextEntry = chronologicalEntries[j];

              // Check if this is a creation event for a descendant
              if (
                (!nextEntry.oldv || Object.keys(nextEntry.oldv).length === 0) &&
                nextEntry.newv?.course_id === courseId &&
                nextEntry.newv?.version_id === versionId &&
                nextEntry.newv?.parent_label_id === parentLabelId
              ) {
                const nextTime = new Date(nextEntry.modified_date).getTime();
                const timeDiff = nextTime - creationTime;

                // Within 5 seconds (batch creation window)
                if (timeDiff >= 0 && timeDiff <= 5000) {
                  descendantIndices.push(j);
                  processedLabelIds.add(nextEntry.newv.label_id);

                  // Recursively find children of this node
                  findDescendants(nextEntry.newv.label_id);
                }
              }
            }
          };

          // Start finding all descendants
          findDescendants(rootLabelId);

          // Add the root entry with all its descendants consolidated
          consolidated.push(currentEntry);
          processedIndices.add(i);

          // Mark all descendants as processed (they won't show as separate entries)
          descendantIndices.forEach(idx => {
            processedIndices.add(idx);
          });
        } else {
          // This is a child without a root parent in the same batch - show separately
          consolidated.push(currentEntry);
          processedIndices.add(i);
        }
      } else {
        // This is an update event (not a creation), add as is
        consolidated.push(currentEntry);
        processedIndices.add(i);
      }
    }

    // Reverse back to original order (newest first for display)
    return consolidated.reverse();
  }

  // Special method to consolidate AssessmentPlan creation events
  private consolidateAssessmentPlanEvents(entries: any[]): any[] {
    const consolidated = [];
    const processedIndices = new Set();

    for (let i = 0; i < entries.length; i++) {
      if (processedIndices.has(i)) continue;

      const currentEntry = entries[i];

      // Check if this is a creation event (oldv is null or empty)
      if (!currentEntry.oldv || Object.keys(currentEntry.oldv).length === 0) {
        // Find all subsequent updates that are part of the initial creation process
        const creationUpdates = [];
        let lastProcessedIndex = i;
        let isCreationComplete = false;

        // Look for all updates that are part of the initial creation process
        for (let j = i + 1; j < entries.length; j++) {
          if (processedIndices.has(j)) continue;

          const nextEntry = entries[j];

          // Check if this is the same assessment
          if (nextEntry.newv?.id !== currentEntry.newv?.id) {
            break;
          }

          // Check if this is a status change to Published (end of initial creation process)
          if (nextEntry.newv?.status === 'Published' ||
            (nextEntry.newv?.is_active !== undefined && nextEntry.oldv?.is_active !== undefined &&
              nextEntry.newv?.is_active === true && nextEntry.oldv?.is_active === false)) {
            creationUpdates.push(nextEntry);
            lastProcessedIndex = j;
            isCreationComplete = true;
            break;
          }

          // Include updates for tags, rules, and other initial creation fields
          const hasInitialCreationChanges = this.hasInitialCreationChanges(nextEntry);
          if (hasInitialCreationChanges) {
            creationUpdates.push(nextEntry);
            lastProcessedIndex = j;
          } else {
            // Check if this is a rules update specifically (might be nested)
            const hasRulesUpdate = this.hasRulesUpdate(nextEntry);
            if (hasRulesUpdate) {
              creationUpdates.push(nextEntry);
              lastProcessedIndex = j;
            } else {
              // If we encounter an update that's not part of initial creation, stop
              break;
            }
          }
        }

        // Merge all initial creation updates into the creation event
        const mergedEntry = { ...currentEntry };
        mergedEntry.newv = { ...currentEntry.newv };

        // Merge all the initial creation updates into the creation event
        creationUpdates.forEach(update => {
          if (update.newv) {
            Object.assign(mergedEntry.newv, update.newv);
          }
        });

        // Update the modified date to the last update's date
        if (creationUpdates.length > 0) {
          const lastUpdate = creationUpdates[creationUpdates.length - 1];
          mergedEntry.modified_date = lastUpdate.modified_date;
          mergedEntry.modified_user = lastUpdate.modified_user;
        }

        consolidated.push(mergedEntry);

        // Mark all processed entries as handled
        processedIndices.add(i);
        for (let k = i + 1; k <= lastProcessedIndex; k++) {
          processedIndices.add(k);
        }
      } else {
        // This is a regular update event (not part of initial creation)
        consolidated.push(currentEntry);
        processedIndices.add(i);
      }
    }

    return consolidated;
  }

  // Helper method to check if an update is part of the initial creation process
  private hasInitialCreationChanges(entry: any): boolean {
    if (!entry.oldv || !entry.newv) return false;

    // Fields that are part of the initial assessment creation process
    const initialCreationFields = [
      'tags',
      'rules',
      'rulesUpdate',
      'status',
      'is_active',
      'modified_user_name',
      'stakeholders',
      'stakeholderCategories',
      'languages',
      'access',
      'count',
      'mode',
      'displayOfQuestions',
      'partialSubmission',
      'jumpSections',
      'jumpQuestions',
      'availableOnMobile',
      'autoEvaluationOfSubjectiveQuestions', // Commented for future use
      // 'singleTimer',
      'showMarkPerQuestion',
      'showDifficultyLevel',
      'showFeedbackQuestions',
      'generateAttendanceSheet',
      'generateResponseSheet',
      'additionalEvidence',
      'captureCandidatesImage',
      'assessmentLogs',
      'enableBrowserTabSwitching',
      'enableAppSwitching',
      'enableMultipleLogins',
      'showCustomerLogo',
      'enableMultipleAttempts',
      'displayOfMultipleResponses',
      'enableGeoFencing',
      'enableSpecialKeys',
      'enableStateTracking',
      'screenRecording',
      'candidatesVideoRecording',
      'IDCardVerification',
      'enableFaceDetection',
      'enableFaceRecognition',
      'enableObjectDetection',
      'enableHeadPoseTracking',
      'captureImageAtRegularIntervals',
      'asynchronousIntegrityCheck',
      'enableChatSupport',
      'enableChatAmongStakeholders',
      'enableRemoteInvigilation',
      'enableRecording',
      'generateFlagsInRealTime',
      'generateWarningsForCandidates',
      'enableTutorials',
      'enablePracticeAssessments',
      'generateHallTicketAdmitCard',
      'showResultsToCandidates',
      'showResponsesToCandidates',
      'notifyCandidatesForResults',
      'generateWarningsForCandidatesInterval',
      'captureImageAtRegularIntervalsInterval',
      'remoteBlocking',
      'forcedScreenSharingAndRecording',
      'videoStreaming',
      'enableSpoofDetection',
      'multiTimer',
      'multipleTimer',
      'multiTimerEnabled',
      'additionalEvidences',
      'thresholdPercentage',
      'faceDetectionThreshold',
      'spoofDetectionThreshold',
      'faceRecognitionThreshold',
      'objectDetectionThreshold',
      'headPoseTrackingThreshold',
      'browserTabSwitchingThreshold',
      'appSwitchingThreshold',
      'multipleLoginThreshold',
      'flagViolationThreshold',
      'enableNoiseDetection',
      'noiseDetectionThreshold',
      'geoFencingData'
    ];

    const changedKeys = this.findChangedKeys(entry.oldv, entry.newv);

    return changedKeys.some(key => initialCreationFields.includes(key));
  }

  // Helper method to check if an update contains rules changes
  private hasRulesUpdate(entry: any): boolean {
    if (!entry.oldv || !entry.newv) return false;

    // Check for direct rules field
    if (entry.newv.rules && typeof entry.newv.rules === 'object') {
      return true;
    }

    // Check for rulesUpdate field
    if (entry.newv.rulesUpdate === true) {
      return true;
    }

    // Check for nested rules structure
    const changedKeys = this.findChangedKeys(entry.oldv, entry.newv);

    // Look for any field that might contain rules data
    const rulesRelatedFields = [
      'rules', 'rulesUpdate', 'mode', 'count', 'access', 'languages',
      'stakeholders', 'stakeholderCategories', 'enableFaceDetection',
      'enableGeoFencing', 'multipleTimer', 'additionalEvidence','autoEvaluationOfSubjectiveQuestions' // Commented for future use
    ];

    return changedKeys.some(key => rulesRelatedFields.includes(key));
  }

  getRevisionHistoryData(data: any) {
    const params = {
      ...data,
      orgId: parseInt(this.orgID, 10),
    };
    return this.api.get(`/revisionHistory/${module[this.parentUrl]}`, params);
  }

  get orgID(): string | null {
    try {
      // 1. First try query parameters
      const queryOrgId = this.route?.snapshot?.queryParamMap?.get('orgId');
      if (queryOrgId) return queryOrgId;

      // 2. Try localStorage for OrgIdForRevision (system admin history)
      const orgIdForRevision = localStorage.getItem('OrgIdForRevision');
      if (orgIdForRevision) {
        try {
          return this.util.decrypt(orgIdForRevision);
        } catch (e) {
          return orgIdForRevision; // If not encrypted, return as-is
        }
      }

      // 3. Try localStorage (handles both encrypted and plain JSON)
      const storedOrg = localStorage.getItem('SelectedOrgForRevision');
      if (storedOrg) {
        if (storedOrg.startsWith('U2FsdGVkX1')) {
          return JSON.parse(this.util.decrypt(storedOrg)).id;
        }
        return JSON.parse(storedOrg).id;
      }

      // 4. Fallback to other localStorage keys
      const value = this.adminUrl ?
        localStorage.getItem('SelectedOrgId') :
        localStorage.getItem('OrgId');

      return this.adminUrl ? value : this.util.decrypt(value);
    } catch (error) {
      console.error('Error retrieving organization ID:', error);
      return null;
    }
  }

}

const module: any = {
  IndustryVerticals: 'industryVertical',
  BusinessVerticals: 'businessVertical',
  Products: 'product',
  CourseGroups: 'courseGroup',
  CourseSubGroups: 'courseSubGroup',
  "org-list": 'org',
  manageAdminHistory: 'orgAdmin',
    manageSystemAdminHistory: 'orgAdmin',
  manageMOUHistory: 'orgMou',
  Roles: 'userRole',
  UserCategory: 'userCategory',
  Users: 'user',
  Leads: 'leadRevision',
  CustomerGroup: 'customerGroup',
  Opportunities: 'opportunityRevision',
  Deals: 'opportunityRevision',
  Customer: 'customerRevision',
  questionPaperVersions: 'questionPaperVersions',
  Scheme: 'schema',
  CourseRevisionHistory: 'courseRevision',
  CourseVersionRevisionHistory: 'courseVersion',
  CourseModuleRevisionHistory: 'courseModule',
  CourseTopicRevisionHistory: 'courseTopic',
  QuestionBank: 'questionBank',
  questions: 'questions',
  Language: 'language',
  courseLevel2Revision: 'courseLevel2Revision',
  courseLevel3Revision: 'courseLevel3Revision',
  QuestionPaperVersions: 'questionPaperVersions',
  QuestionPapers: 'questionPaper',
  RateMaster: 'rateMaster',
  Invoices: 'invoices',
  Bills: 'bills',
  Payments: 'payments',
  Receipts: 'receipts',
  Project: 'project',
  ExpenseTypes: 'expenseTypes',
  AssessmentPlan: 'assessmentPlan',
  assessmentPlan: 'assessmentPlan',
  Candidate: 'candidates',
  EvaluationCriteria: 'evaluationCriteria',
  apiIntegration: 'apiIntegration',
  labelRevisionHistory: 'label',
  label: 'label',
  subscriptionPlans: 'subscriptionPlans',
  tags: 'tags',
}